import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Lock, Play, CheckCircle, Clock, ChevronRight, ChevronDown, Video, FileText, X, ClipboardList, Calendar, Droplet, Sparkles, Star, StickyNote, Save, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActivityTracker } from '../hooks/useActivityTracker';
import WellnessSnapshotQuestionnaire from '../components/WellnessSnapshotQuestionnaire';
import CompletionReflection from '../components/CompletionReflection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default program start date — used when a user has no personal enrollment.
// Program cohort begins May 1, 2026.
const DEFAULT_PROGRAM_START_DATE = new Date(2026, 4, 1); // May 1, 2026

// Helper function to check if a day is unlocked based on start date
const isDayUnlocked = (dayId, startDate) => {
  // Introduction (id 1) and Wellness Snapshot (id 2) are always unlocked
  if (dayId <= 2 || dayId === 100 || dayId === 101) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const base = new Date(startDate);
  base.setHours(0, 0, 0, 0);

  // Day 1 (id 3) unlocks on start date, Day 2 (id 4) on start + 1, etc.
  const dayNumber = dayId - 2;
  const unlockDate = new Date(base);
  unlockDate.setDate(unlockDate.getDate() + dayNumber - 1);

  return today >= unlockDate;
};

// Helper function to get unlock date for a day
const getUnlockDate = (dayId, startDate) => {
  if (dayId <= 2 || dayId === 100 || dayId === 101) return null;
  const dayNumber = dayId - 2;
  const base = new Date(startDate);
  base.setHours(0, 0, 0, 0);
  const unlockDate = new Date(base);
  unlockDate.setDate(unlockDate.getDate() + dayNumber - 1);
  return unlockDate;
};

// Format date nicely
const formatDate = (date) => {
  if (!date) return '';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Program content - 31 placeholders
const programDays = [
  {
    id: 1,
    title: 'Introduction',
    subtitle: 'Welcome to Your 28 Day Stress Reset Journey',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/XaPZOW12VBA',
    completed: false,
  },
  {
    id: 2,
    title: 'Wellness Snapshot',
    subtitle: 'Establishing Your Starting Point',
    description: '',
    videoUrl: '',
    completed: false,
  },
  {
    id: 100,
    title: 'The Experience Guide',
    subtitle: 'What to Expect on This Journey',
    description: '',
    videoUrl: '',
    completed: false,
    richContent: true,
  },
  {
    id: 101,
    title: 'Fascial Facial Maneuvers',
    subtitle: 'Energy and Release',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/HzdcBMxdvJ8',
    completed: false,
    richContent: true,
  },
  {
    id: 3,
    title: 'Day 1',
    subtitle: 'Regulation, Hydration and Detox',
    videoContent: 'Stress Reset Traditional — Palate Swipe, Totally Twisted, Anti Gravity',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/D-K8yN7y7IU',
  },
  {
    id: 4,
    title: 'Day 2',
    subtitle: 'Change Is Inevitable',
    videoContent: 'Stress Reset Traditional — Palate Swipe, Totally Twisted, Anti Gravity',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/RSR7NpKKfoM',
    completed: false,
    richContent: true,
  },
  {
    id: 5,
    title: 'Day 3',
    subtitle: 'Intention',
    videoContent: 'Vagus Nerve Reset — Ankles, Knees, Hamstrings, Psoas, Jaw',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/hs9RROigS0Q',
    completed: false,
    richContent: true,
  },
  {
    id: 6,
    title: 'Day 4',
    subtitle: 'Organ Reset',
    videoContent: 'Standing with Descriptions',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/EcEkqCtqSx8',
    completed: false,
    richContent: true,
  },
  {
    id: 7,
    title: 'Day 5',
    subtitle: 'Stress Reset',
    videoContent: 'Palate Swipe, Totally Twisted, Psoas Release, Jaw Release',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/jzOa6eOQKDM',
    completed: false,
    richContent: true,
  },
  {
    id: 8,
    title: 'Day 6',
    subtitle: 'Beach Stress Reset',
    videoContent: 'Totally Twisted, Swinger, Psoas Release, Scalene Release',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/8tPrX2GgL_A',
    completed: false,
    richContent: true,
  },
  {
    id: 9,
    title: 'Day 7',
    subtitle: 'Lying Down Organ Reset',
    videoContent: 'Organs & Stored Emotions',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/hv--9WSB0l4',
    completed: false,
    richContent: true,
  },
  {
    id: 10,
    title: 'Day 8',
    subtitle: 'Stress Reset',
    videoContent: 'Vagus Nerve Reset, Totally Twisted, Anti Gravity — 16 Mins',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/2H5G_xddmTQ',
    completed: false,
    richContent: true,
  },
  {
    id: 11,
    title: 'Day 9',
    subtitle: 'Stress Reset',
    videoContent: 'Totally Twisted, Swinger, Pull Over, Sinus Reset',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/XMdErCVOOUs',
    completed: false,
    richContent: true,
  },
  {
    id: 12,
    title: 'Day 10',
    subtitle: 'Restorative Organ Reset',
    description: 'Restorative all Organs',
    videoUrl: 'https://www.youtube.com/embed/yimMFzsv2_I',
    completed: false,
    richContent: true,
  },
  {
    id: 13,
    title: 'Day 11',
    subtitle: 'Fascial Coffee - Upper Body Reset',
    description: 'Twisty Wristy, Elbow Torque, Psoas Release, Vagus Nerve Reset',
    videoUrl: 'https://www.youtube.com/embed/b38UD_SyY3U',
    videoContent: 'Twisty Wristy, Elbow Torque, Psoas Release, Vagus Nerve Reset',
    completed: false,
    richContent: true,
  },
  {
    id: 14,
    title: 'Day 12',
    subtitle: 'Stress Release',
    description: 'Why we use the 3-6-9 breath cycle and the layers within Fascial Maneuvers.',
    videoUrl: 'https://www.youtube.com/embed/TK7mUqKEI4A',
    videoContent: 'Totally Twisted, Anti Gravity, Chest Release',
    completed: false,
    richContent: true,
  },
  {
    id: 15,
    title: 'Day 13',
    subtitle: 'Organ Reset',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/EcEkqCtqSx8',
    completed: false,
  },
  {
    id: 16,
    title: 'Day 14',
    subtitle: 'All Healing Is Self Healing',
    description: 'Why the body holds the blueprint.',
    videoUrl: 'https://www.youtube.com/embed/T4_BPLooxpI',
    videoContent: 'Why the Body Holds the Blueprint',
    completed: false,
    richContent: true,
  },
  {
    id: 17,
    title: 'Day 15',
    subtitle: 'Facial Fascial Massage',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/pEOet1cf_z8',
    videoContent: 'Totally Twisted, Anti Gravity, Ear Pull/Twist',
    completed: false,
  },
  {
    id: 18,
    title: 'Day 16',
    subtitle: 'Stress Reset',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/HvghPnO8HEg',
    videoContent: 'Palate Swipe, Totally Twisted, Swinger, Pullover, Ear Pull',
    completed: false,
  },
  {
    id: 19,
    title: 'Day 17',
    subtitle: 'Stress Reset',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/TlGdCo0SSHY',
    videoContent: 'Totally Twisted, Ankles, Knees, Psoas, Collar Bone',
    completed: false,
  },
  {
    id: 20,
    title: 'Day 18',
    subtitle: 'Stress Reset',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/wAXBKPzH9X4',
    videoContent: 'Totally Twisted, Pretzel Squat, Chest Release',
    completed: false,
    richContent: true,
  },
  {
    id: 21,
    title: 'Day 19',
    subtitle: 'Beach Reset',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/8tPrX2GgL_A',
    completed: false,
  },
  {
    id: 22,
    title: 'Day 20',
    subtitle: 'Extremities Practice',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/0uL4BTVS3gk',
    videoContent: 'Ankles, Achilles, Back of Knee, Front of Knee, Elbow Torque, Collarbone, Nasal Bone, Ear Pull',
    completed: false,
  },
  {
    id: 23,
    title: 'Day 21',
    subtitle: 'Stress Reset - Chair Practice',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/qfDJOckl2jk',
    completed: false,
    richContent: true,
  },
  {
    id: 24,
    title: 'Day 22',
    subtitle: 'Stress Reset - Totally Twisted, Anti Gravity, Cheek Release, Sinus Release',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/1g9XUe05lUg',
    videoContent: 'Stress Reset',
    completed: false,
  },
  {
    id: 25,
    title: 'Day 23',
    subtitle: 'Organ Reset',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/4HlaO_q08Ao',
    videoContent: 'Organ Reset - With Affirmations',
    completed: false,
  },
  {
    id: 26,
    title: 'Day 24',
    subtitle: 'Stress Reset - Totally Twisted, Anti Gravity, Elbow Torque, Forehead Release',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/c-8RuEgU3Fw',
    videoContent: 'Stress Reset',
    completed: false,
  },
  {
    id: 27,
    title: 'Day 25',
    subtitle: 'Vagus Nerve, Totally Twisted, Swinger, Twisty Wristy',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/oE3vST404f0',
    videoContent: 'Stress Reset',
    completed: false,
  },
  {
    id: 28,
    title: 'Day 26',
    subtitle: 'Lower Body Reset - Vagus Nerve, Ankles, Achilles, Knees, Back of Knees, Psoas, Jaw',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/hs9RROigS0Q',
    videoContent: 'Lower Body Reset',
    completed: false,
  },
  {
    id: 29,
    title: 'Day 27',
    subtitle: 'Stress Reset 16mins - Vagus Nerve Reset, Around the World Totally Twisted, Anti Gravity',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/2H5G_xddmTQ',
    videoContent: 'Stress Reset 16mins',
    completed: false,
  },
  {
    id: 30,
    title: 'Day 28',
    subtitle: 'Stress Reset - Vagus Nerve, Fascial Coffee, Totally Twisted, Anti Gravity, Twisty Wristy, Ear Twist',
    description: '',
    videoUrl: 'https://www.youtube.com/embed/lZAp-Dj4Prc',
    videoContent: 'Stress Reset',
    completed: false,
  },
  {
    id: 31,
    title: 'Conclusion & Final Wellness Snapshot',
    subtitle: 'Celebrating Your Transformation',
    description: '',
    videoUrl: '',
    completed: false,
  },
  {
    id: 32,
    title: 'Review & Reflection',
    subtitle: '28 Day Reset — Completion Reflection',
    description: '',
    videoUrl: '',
    completed: false,
  },
];

const FasciaResetProgramPage = () => {
  const { user, hasAccess } = useAuth();
  const [selectedDay, setSelectedDay] = useState(null);
  const [expandedSection, setExpandedSection] = useState('all');
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [finalSnapshot, setFinalSnapshot] = useState(null);
  const [loadingSnapshots, setLoadingSnapshots] = useState(true);

  // Per-user program start date (null = not enrolled → use default)
  const [programStartDate, setProgramStartDate] = useState(DEFAULT_PROGRAM_START_DATE);
  const [enrollment, setEnrollment] = useState(null);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [restartInFlight, setRestartInFlight] = useState(false);

  // Notes & Favorites state
  const [notes, setNotes] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [currentNote, setCurrentNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [journalNote, setJournalNote] = useState('');
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);

  // Track user activity
  const { trackView, trackVideoPlay, trackLinkClick, trackComplete, trackSectionClick } = useActivityTracker('fascia-reset');

  const handleSelectDay = (day) => {
    setSelectedDay(day);
    trackView(day.id, day.title + (day.subtitle ? ` - ${day.subtitle}` : ''));
  };

  // Check if user has access to this program
  const hasProgramAccess = hasAccess('fascia-reset');

  // Fetch per-user enrollment (start date); fall back to default if none
  useEffect(() => {
    if (!user?.id) return;
    const fetchEnrollment = async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/program-enrollment/${user.id}/fascia-reset`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.enrollment?.started_at) {
            setEnrollment(data.enrollment);
            const d = new Date(data.enrollment.started_at);
            d.setHours(0, 0, 0, 0);
            setProgramStartDate(d);
          }
        }
      } catch (err) {
        console.error('Error fetching enrollment:', err);
      }
    };
    fetchEnrollment();
  }, [user?.id]);

  const handleRestartProgram = async () => {
    if (!user?.id) return;
    setRestartInFlight(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/program-enrollment/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, program_id: 'fascia-reset' }),
      });
      if (res.ok) {
        const data = await res.json();
        const newStart = new Date(data.started_at);
        newStart.setHours(0, 0, 0, 0);
        setProgramStartDate(newStart);
        setEnrollment((prev) => ({
          ...(prev || {}),
          started_at: data.started_at,
          restart_count: data.restart_count,
        }));
        setSelectedDay(null);
      }
    } catch (err) {
      console.error('Error restarting program:', err);
    }
    setRestartInFlight(false);
    setShowRestartConfirm(false);
  };

  // Fetch existing snapshots
  useEffect(() => {
    const fetchSnapshots = async () => {
      if (!user?.id) {
        setLoadingSnapshots(false);
        return;
      }

      try {
        const [initialRes, finalRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/wellness-snapshot/${user.id}/initial`),
          fetch(`${BACKEND_URL}/api/wellness-snapshot/${user.id}/final`)
        ]);

        if (initialRes.ok) {
          const data = await initialRes.json();
          if (data) setInitialSnapshot(data);
        }
        if (finalRes.ok) {
          const data = await finalRes.json();
          if (data) setFinalSnapshot(data);
        }
      } catch (error) {
        console.error('Error fetching snapshots:', error);
      } finally {
        setLoadingSnapshots(false);
      }
    };

    fetchSnapshots();
  }, [user?.id]);

  // Fetch notes and favorites
  useEffect(() => {
    if (!user?.id) return;
    const fetchNotesAndFavs = async () => {
      try {
        const [notesRes, favsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/user/notes/${user.id}/fascia-reset`),
          fetch(`${BACKEND_URL}/api/user/favorites/${user.id}/fascia-reset`),
        ]);
        if (notesRes.ok) {
          const data = await notesRes.json();
          setNotes(data.notes || {});
          if (data.notes && data.notes[0]) setJournalNote(data.notes[0]);
        }
        if (favsRes.ok) {
          const data = await favsRes.json();
          setFavorites(data.favorites || []);
        }
      } catch (e) {
        console.error('Error fetching notes/favorites:', e);
      }
    };
    fetchNotesAndFavs();
  }, [user?.id]);

  // Set current note when selecting a day
  useEffect(() => {
    if (selectedDay) {
      setCurrentNote(notes[selectedDay.id] || '');
      setNoteSaved(false);
    }
  }, [selectedDay?.id, notes]);

  const saveNote = async () => {
    if (!user?.id || !selectedDay) return;
    setNoteSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/user/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, program: 'fascia-reset', lesson_id: selectedDay.id, note: currentNote }),
      });
      setNotes(prev => ({ ...prev, [selectedDay.id]: currentNote }));
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2000);
    } catch (e) {
      console.error('Error saving note:', e);
    } finally {
      setNoteSaving(false);
    }
  };

  const toggleFavorite = async (lessonId) => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/favorites/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, program: 'fascia-reset', lesson_id: lessonId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'added') {
          setFavorites(prev => [...prev, lessonId]);
        } else {
          setFavorites(prev => prev.filter(id => id !== lessonId));
        }
      }
    } catch (e) {
      console.error('Error toggling favorite:', e);
    }
  };

  const saveJournal = async () => {
    if (!user?.id) return;
    setJournalSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/user/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, program: 'fascia-reset', lesson_id: 0, note: journalNote }),
      });
      setJournalSaved(true);
      setTimeout(() => setJournalSaved(false), 2000);
    } catch (e) {
      console.error('Error saving journal:', e);
    } finally {
      setJournalSaving(false);
    }
  };

  // If not logged in or no access, show locked state
  if (!user || !hasProgramAccess) {
    return (
      <div className="bg-cream min-h-screen pt-24" data-testid="fascia-reset-locked">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <Link
            to="/programs"
            className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-8"
          >
            <ArrowLeft size={18} />
            <span className="font-body text-sm">Back to Programs</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Locked Hero */}
            <div className="relative rounded-2xl overflow-hidden mb-8">
              <img
                src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800"
                alt="28 Day Stress Reset"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-forest/70 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock size={40} className="text-white" />
                  </div>
                  <h1 className="font-heading text-4xl text-white mb-2">28 Day Stress Reset</h1>
                  <p className="text-cream/80 font-body">This program requires purchase to access</p>
                </div>
              </div>
            </div>

            {/* Program Info */}
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <h2 className="font-heading text-2xl text-forest mb-4">Feel the Fascia Miracle</h2>
              <p className="text-stone-600 font-body mb-6">
                Discover the incredible world of fascia and how this connective tissue system impacts every aspect of your health. 
                Over 28 days, you'll learn targeted techniques to release fascial restrictions and restore fluid movement.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <div className="flex items-center gap-2 text-stone-500">
                  <Play size={18} className="text-terracotta" />
                  <span className="font-body">32 lessons</span>
                </div>
                <div className="flex items-center gap-2 text-stone-500">
                  <Clock size={18} className="text-terracotta" />
                  <span className="font-body">28 days + intro & conclusion</span>
                </div>
                <div className="flex items-center gap-2 text-stone-500">
                  <Video size={18} className="text-terracotta" />
                  <span className="font-body">Video lessons included</span>
                </div>
              </div>

              <div className="bg-terracotta/10 rounded-xl p-6">
                <h3 className="font-heading text-lg text-forest mb-2">Ready to Transform?</h3>
                <p className="text-stone-600 font-body text-sm mb-4">
                  {!user 
                    ? 'Please sign in or create an account, then contact us to purchase this program.'
                    : 'Contact us to purchase this program and unlock all 32 lessons.'}
                </p>
                {!user ? (
                  <Link
                    to="/programs"
                    className="inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors"
                  >
                    Sign In to Get Started
                    <ChevronRight size={18} />
                  </Link>
                ) : (
                  <a
                    href="mailto:heyjbennett@yahoo.ca?subject=28 Day Stress Reset Program Purchase"
                    className="inline-flex items-center gap-2 bg-terracotta text-white px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors"
                  >
                    Contact to Purchase
                    <ChevronRight size={18} />
                  </a>
                )}
              </div>
            </div>

            {/* Preview of What's Included */}
            <div className="bg-stone-100 rounded-2xl p-8">
              <h3 className="font-heading text-xl text-forest mb-6">What's Included</h3>
              <div className="grid md:grid-cols-3 gap-4 text-left">
                <div className="bg-white rounded-xl p-4">
                  <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center mb-3">
                    <FileText size={20} className="text-terracotta" />
                  </div>
                  <h4 className="font-heading text-forest mb-1">Introduction</h4>
                  <p className="text-stone-500 font-body text-sm">Welcome and program overview</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle size={20} className="text-terracotta" />
                  </div>
                  <h4 className="font-heading text-forest mb-1">Wellness Snapshots</h4>
                  <p className="text-stone-500 font-body text-sm">Track your transformation</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center mb-3">
                    <Video size={20} className="text-terracotta" />
                  </div>
                  <h4 className="font-heading text-forest mb-1">28 Daily Lessons</h4>
                  <p className="text-stone-500 font-body text-sm">Video + written guidance</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // User has access - show full program
  const sections = [
    { id: 'intro', title: 'Getting Started', days: programDays.slice(0, 4) },
    { id: 'week1', title: 'Week 1 (Days 1-7)', days: programDays.slice(4, 11) },
    { id: 'week2', title: 'Week 2 (Days 8-14)', days: programDays.slice(11, 18) },
    { id: 'week3', title: 'Week 3 (Days 15-21)', days: programDays.slice(18, 25) },
    { id: 'week4', title: 'Week 4 (Days 22-28)', days: programDays.slice(25, 32) },
    { id: 'conclusion', title: 'Conclusion', days: programDays.slice(32, 34) },
  ];

  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="fascia-reset-program">
      {/* Hero Section */}
      <section className="relative">
        <div className="h-64 md:h-80 relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200"
            alt="28 Day Stress Reset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/60 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto px-6 md:px-12 pb-8 w-full">
              <Link
                to="/programs"
                className="inline-flex items-center gap-2 text-cream/70 hover:text-cream transition-colors mb-4"
              >
                <ArrowLeft size={18} />
                <span className="font-body text-sm">Back to Programs</span>
              </Link>
              <h1 className="font-heading text-4xl md:text-5xl text-white mb-2">28 Day Stress Reset</h1>
              <p className="text-cream/80 font-body text-lg">Feel the Fascia Miracle and Kick Start Healing at a deeper level</p>
            </div>
          </div>
        </div>
      </section>

      {/* Progress Bar */}
      <section className="bg-white border-b border-stone-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <span className="text-stone-500 font-body text-sm">Your Progress</span>
              <div className="w-48 h-2 bg-stone-200 rounded-full overflow-hidden">
                <div className="w-0 h-full bg-terracotta rounded-full transition-all duration-500" />
              </div>
              <span className="text-stone-500 font-body text-sm">0 / 32 completed</span>
            </div>
            <div className="flex items-center gap-3 text-forest flex-wrap">
              {enrollment?.restart_count > 0 && (
                <span
                  className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 rounded-full px-3 py-1 text-xs font-body font-medium"
                  data-testid="round-badge"
                  title={`You've restarted ${enrollment.restart_count} time${enrollment.restart_count === 1 ? '' : 's'}`}
                >
                  <Sparkles size={11} />
                  Round {enrollment.restart_count + 1}
                </span>
              )}
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span className="font-body text-sm" data-testid="program-day-status">
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (today < programStartDate) {
                      return `Day 1 begins ${formatDate(programStartDate)}`;
                    }
                    const daysSinceStart = Math.floor((today - programStartDate) / (1000 * 60 * 60 * 24));
                    const currentDay = Math.min(daysSinceStart + 1, 28);
                    const startedLabel = `Started ${formatDate(programStartDate)}`;
                    if (currentDay >= 28) {
                      return `${startedLabel} · All 28 days unlocked`;
                    }
                    return `${startedLabel} · Day ${currentDay} of 28`;
                  })()}
                </span>
              </div>
              {hasProgramAccess && user && (
                <button
                  onClick={() => setShowRestartConfirm(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-body text-stone-500 hover:text-forest border border-stone-300 hover:border-forest rounded-full px-3 py-1 transition-colors"
                  data-testid="restart-program-button"
                  title="Restart the 28-day journey with today as Day 1"
                >
                  <RotateCcw size={12} />
                  Restart
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Sidebar - Day List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden sticky top-36">
                <div className="bg-forest p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-heading text-xl text-white">Program Content</h2>
                      <p className="text-cream/70 font-body text-sm">32 lessons over 28 days</p>
                    </div>
                    {favorites.length > 0 && (
                      <button
                        onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body transition-colors ${
                          showFavoritesOnly ? 'bg-amber-400 text-forest' : 'bg-white/10 text-cream/80 hover:bg-white/20'
                        }`}
                        data-testid="favorites-filter"
                      >
                        <Star size={12} className={showFavoritesOnly ? 'fill-forest' : ''} />
                        {showFavoritesOnly ? `Starred (${favorites.length})` : 'Starred'}
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="max-h-[60vh] overflow-y-auto">
                  {sections.map((section, sectionIndex) => (
                    <React.Fragment key={section.id}>
                      <div className="border-b border-stone-100 last:border-0">
                      <button
                        onClick={() => setExpandedSection(expandedSection === section.id ? 'all' : section.id)}
                        className="w-full flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
                      >
                        <span className="font-body font-medium text-forest">{section.title}</span>
                        <ChevronDown
                          size={18}
                          className={`text-stone-400 transition-transform ${
                            expandedSection === section.id || expandedSection === 'all' ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      <AnimatePresence>
                        {(expandedSection === section.id || expandedSection === 'all') && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            {section.days.filter(day => !showFavoritesOnly || favorites.includes(day.id)).map((day) => {
                              const unlocked = isDayUnlocked(day.id, programStartDate);
                              const unlockDate = getUnlockDate(day.id, programStartDate);
                              
                              return (
                              <button
                                key={day.id}
                                onClick={() => unlocked && handleSelectDay(day)}
                                disabled={!unlocked}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                                  !unlocked 
                                    ? 'opacity-60 cursor-not-allowed bg-stone-50' 
                                    : selectedDay?.id === day.id 
                                      ? 'bg-terracotta/10 border-l-4 border-terracotta hover:bg-terracotta/15' 
                                      : 'hover:bg-terracotta/5'
                                }`}
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  !unlocked
                                    ? 'bg-stone-200 text-stone-400'
                                    : day.completed 
                                      ? 'bg-emerald-100 text-emerald-600' 
                                      : 'bg-stone-100 text-stone-400'
                                }`}>
                                  {!unlocked ? (
                                    <Lock size={14} />
                                  ) : day.completed ? (
                                    <CheckCircle size={16} />
                                  ) : (
                                    <span className="text-xs font-body font-medium">{day.id === 100 ? 3 : day.id === 101 ? 4 : day.id}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`font-body text-sm truncate ${!unlocked ? 'text-stone-400' : 'text-forest'}`}>{day.title}</p>
                                  {!unlocked && unlockDate ? (
                                    <p className="font-body text-xs text-stone-400 truncate flex items-center gap-1">
                                      <Calendar size={10} />
                                      Unlocks {formatDate(unlockDate)}
                                    </p>
                                  ) : day.subtitle && (
                                    <p className="font-body text-xs text-stone-600 truncate">{day.subtitle}</p>
                                  )}
                                  {unlocked && day.videoContent && (
                                    <p className="font-body text-xs text-terracotta/70 truncate">{day.videoContent}</p>
                                  )}
                                </div>
                                {unlocked && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleFavorite(day.id); }}
                                    className="flex-shrink-0 p-1 hover:scale-110 transition-transform"
                                    data-testid={`star-lesson-${day.id}`}
                                  >
                                    <Star size={14} className={favorites.includes(day.id) ? 'fill-amber-400 text-amber-400' : 'text-stone-300'} />
                                  </button>
                                )}
                              </button>
                            );})}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    {/* My Journal entry after Getting Started */}
                    {sectionIndex === 0 && (
                      <div className="border-b border-stone-100">
                        <button
                          onClick={() => setSelectedDay({ id: 'journal', title: 'My Journal' })}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            selectedDay?.id === 'journal'
                              ? 'bg-amber-50 border-l-4 border-amber-400'
                              : 'hover:bg-amber-50/50'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <StickyNote size={14} className="text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-body text-sm text-forest font-medium">My Journal</p>
                            <p className="font-body text-xs text-terracotta/70">Your personal notes & reflections</p>
                          </div>
                        </button>
                      </div>
                    )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-2">
              {/* Daily Zoom Link Card */}
              <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl p-4 mb-6 shadow-lg" data-testid="daily-zoom-link">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Video size={20} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 font-body text-xs uppercase tracking-wide">Daily Live Session</p>
                    <p className="text-white font-heading text-lg">Join Zoom</p>
                  </div>
                  <a
                    href="https://us06web.zoom.us/j/84480134733?pwd=jIbuZ7WMeusCvROELxBAkn4AOthakh.1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-violet-600 px-4 py-2 rounded-full font-body text-sm font-medium hover:bg-violet-50 transition-colors flex items-center gap-2"
                    data-testid="zoom-join-button"
                  >
                    Join Now
                    <ChevronRight size={16} />
                  </a>
                </div>
              </div>
              {selectedDay ? (
                selectedDay.id === 'journal' ? (
                  <motion.div
                    key="journal"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-amber-600 to-amber-500 p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <StickyNote size={24} className="text-white" />
                          <div>
                            <h2 className="font-heading text-3xl text-white">My Journal</h2>
                            <p className="text-white/80 font-body mt-1">Your personal notes & reflections for this program</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedDay(null)}
                          className="text-white/70 hover:text-white transition-colors lg:hidden"
                        >
                          <X size={24} />
                        </button>
                      </div>
                    </div>
                    <div className="p-6 md:p-8">
                      <p className="text-stone-500 font-body text-sm mb-4">
                        Use this space to capture your overall thoughts, experiences, and reflections throughout the 28-day reset. Each lesson also has its own notes section at the bottom.
                      </p>
                      <textarea
                        value={journalNote}
                        onChange={(e) => setJournalNote(e.target.value)}
                        placeholder="Write about your journey — how you're feeling, what you're noticing, breakthroughs, challenges, anything that comes to mind..."
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl p-4 text-forest font-body text-sm min-h-[300px] resize-y focus:outline-none focus:border-amber-400 placeholder:text-stone-400"
                        data-testid="journal-input"
                      />
                      <div className="flex items-center justify-between mt-4">
                        <p className="text-stone-400 font-body text-xs">
                          {journalNote.length > 0 ? `${journalNote.split(/\s+/).filter(Boolean).length} words` : 'Start writing...'}
                        </p>
                        <div className="flex items-center gap-2">
                          {journalSaved && <span className="text-emerald-500 font-body text-sm">Saved!</span>}
                          <button
                            onClick={saveJournal}
                            disabled={journalSaving}
                            className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-full font-body text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                            data-testid="save-journal-btn"
                          >
                            <Save size={14} />
                            {journalSaving ? 'Saving...' : 'Save Journal'}
                          </button>
                        </div>
                      </div>

                      {/* Quick links to lesson notes */}
                      {Object.keys(notes).filter(k => k !== '0').length > 0 && (
                        <div className="mt-8 border-t border-stone-200 pt-6">
                          <h4 className="font-heading text-forest mb-3">Your Lesson Notes</h4>
                          <div className="space-y-2">
                            {Object.entries(notes).filter(([k]) => k !== '0').map(([lessonId, note]) => {
                              const lesson = programDays.find(d => d.id === parseInt(lessonId));
                              if (!lesson || !note) return null;
                              return (
                                <button
                                  key={lessonId}
                                  onClick={() => handleSelectDay(lesson)}
                                  className="w-full text-left bg-stone-50 hover:bg-stone-100 rounded-xl p-3 transition-colors"
                                >
                                  <p className="font-body text-forest text-sm font-medium">{lesson.title}{lesson.subtitle ? ` — ${lesson.subtitle}` : ''}</p>
                                  <p className="text-stone-500 font-body text-xs mt-1 line-clamp-2">{note}</p>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ) : (
                <motion.div
                  key={selectedDay.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  {/* Day Header */}
                  <div className="bg-gradient-to-r from-forest to-forest-dark p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-cream/70 font-body text-sm mb-1">Lesson {selectedDay.id === 100 ? 3 : selectedDay.id === 101 ? 4 : selectedDay.id} of 32</p>
                        <h2 className="font-heading text-3xl text-white">{selectedDay.title}</h2>
                        {selectedDay.subtitle && (
                          <p className="text-cream/80 font-body mt-1">{selectedDay.subtitle}</p>
                        )}
                      </div>
                      <button
                        onClick={() => setSelectedDay(null)}
                        className="text-cream/70 hover:text-cream transition-colors lg:hidden"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  </div>

                  {/* Wellness Snapshot Questionnaire for Day 2 (Initial) */}
                  {selectedDay.id === 2 && (
                    <div className="p-6 md:p-8">
                      {loadingSnapshots ? (
                        <div className="text-center py-12">
                          <div className="w-12 h-12 border-4 border-terracotta/30 border-t-terracotta rounded-full animate-spin mx-auto mb-4" />
                          <p className="text-stone-500 font-body">Loading your wellness snapshot...</p>
                        </div>
                      ) : (
                        <WellnessSnapshotQuestionnaire
                          userId={user?.id}
                          snapshotType="initial"
                          existingSnapshot={initialSnapshot}
                          onComplete={(data) => {
                            setInitialSnapshot(data);
                            trackSectionClick('wellness_snapshot_initial', 'Wellness Snapshot - Initial');
                          }}
                        />
                      )}
                    </div>
                  )}

                  {/* Wellness Snapshot Questionnaire for Day 31 (Final) */}
                  {selectedDay.id === 31 && (
                    <div className="p-6 md:p-8">
                      {/* Heartfelt Closing Message */}
                      <div className="mb-10 space-y-6">
                        <div className="text-center mb-8">
                          <p className="font-heading text-3xl sm:text-4xl text-forest mb-4">It's hard to believe this is the end!</p>
                          <p className="text-stone-600 font-body text-lg leading-relaxed max-w-2xl mx-auto">
                            I've thoroughly enjoyed and I hope you have also!
                          </p>
                        </div>

                        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-100 space-y-4">
                          <p className="text-stone-600 font-body leading-relaxed">
                            We would love to hear feedback from you and maybe even a testimonial. 
                            As we are trying to build... we want to keep creating more and more wellness programs and make them accessible also.
                          </p>
                          <p className="text-forest font-body font-medium leading-relaxed">
                            Share with friends and family and get people involved.
                          </p>
                        </div>

                        <div className="bg-forest text-cream rounded-2xl p-6 sm:p-8 text-center space-y-4">
                          <p className="font-body text-cream text-lg leading-relaxed">
                            We need our wellness more than ever before and we need to do it together.
                          </p>
                          <p className="font-body text-cream/90 leading-relaxed">
                            It's the time to connect now and be in community and healing and embracing our elevated wellness.
                          </p>
                          <p className="font-accent text-xl text-terracotta italic">
                            There's such a strong empowerment in guiding our own wellness.
                          </p>
                        </div>

                        <div className="bg-terracotta/10 rounded-2xl p-6 sm:p-8 text-center border border-terracotta/20">
                          <p className="font-heading text-2xl text-forest mb-2">We appreciate you being here</p>
                          <p className="text-stone-600 font-body leading-relaxed">
                            We will keep you posted about ongoing programs.
                          </p>
                        </div>
                      </div>

                      {loadingSnapshots ? (
                        <div className="text-center py-12">
                          <div className="w-12 h-12 border-4 border-terracotta/30 border-t-terracotta rounded-full animate-spin mx-auto mb-4" />
                          <p className="text-stone-500 font-body">Loading your wellness snapshot...</p>
                        </div>
                      ) : (
                        <>
                          {/* Show comparison if both snapshots exist */}
                          {initialSnapshot && finalSnapshot && (
                            <div className="bg-emerald-50 rounded-xl p-6 mb-8 border border-emerald-200">
                              <h3 className="font-heading text-lg text-forest mb-4">Your Transformation Journey</h3>
                              <div className="grid grid-cols-2 gap-6 text-center">
                                <div>
                                  <p className="text-stone-500 font-body text-sm mb-1">Starting Score</p>
                                  <p className="font-heading text-3xl text-forest">{initialSnapshot.total_score}</p>
                                </div>
                                <div>
                                  <p className="text-stone-500 font-body text-sm mb-1">Final Score</p>
                                  <p className="font-heading text-3xl text-terracotta">{finalSnapshot.total_score}</p>
                                </div>
                              </div>
                              {initialSnapshot.total_score > finalSnapshot.total_score && (
                                <p className="text-emerald-700 font-body text-center mt-4">
                                  🎉 Amazing! You've improved by {initialSnapshot.total_score - finalSnapshot.total_score} points!
                                </p>
                              )}
                            </div>
                          )}
                          <WellnessSnapshotQuestionnaire
                            userId={user?.id}
                            snapshotType="final"
                            existingSnapshot={finalSnapshot}
                            onComplete={(data) => {
                              setFinalSnapshot(data);
                              trackSectionClick('wellness_snapshot_final', 'Wellness Snapshot - Final');
                            }}
                          />
                        </>
                      )}
                    </div>
                  )}

                  {selectedDay.id === 32 && (
                    <div className="p-6 md:p-8">
                      <CompletionReflection userId={user?.id} program="fascia-reset" />
                    </div>
                  )}

                  {/* Regular content for other days */}
                  {/* Regular content for other days (excluding Intro, Wellness Snapshot, Conclusion, and Day 1) */}
                  {selectedDay.id !== 2 && selectedDay.id !== 31 && selectedDay.id !== 32 && selectedDay.id !== 1 && selectedDay.id !== 3 && selectedDay.id !== 4 && selectedDay.id !== 5 && selectedDay.id !== 6 && selectedDay.id !== 7 && selectedDay.id !== 8 && selectedDay.id !== 9 && selectedDay.id !== 10 && selectedDay.id !== 11 && selectedDay.id !== 12 && selectedDay.id !== 13 && selectedDay.id !== 14 && selectedDay.id !== 16 && selectedDay.id !== 20 && selectedDay.id !== 23 && selectedDay.id !== 100 && selectedDay.id !== 101 && (
                    <>
                      {/* Video Placeholder */}
                      <div className="aspect-video bg-stone-900 flex items-center justify-center">
                        {selectedDay.videoUrl ? (
                          <iframe
                            src={selectedDay.videoUrl}
                            title={selectedDay.title}
                            className="w-full h-full"
                            allowFullScreen
                          />
                        ) : (
                          <div className="text-center">
                            <div className="w-20 h-20 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Video size={32} className="text-stone-600" />
                            </div>
                            <p className="text-stone-500 font-body">Video coming soon</p>
                            <p className="text-stone-600 font-body text-sm mt-1">Content is being prepared</p>
                          </div>
                        )}
                      </div>

                      {/* Content Area */}
                      <div className="p-6 md:p-8">
                        {selectedDay.description ? (
                          <div className="prose prose-stone max-w-none">
                            <p className="text-stone-600 font-body leading-relaxed whitespace-pre-line">
                              {selectedDay.description}
                            </p>
                          </div>
                        ) : (
                          <div className="bg-stone-50 rounded-xl p-8 text-center">
                            <FileText size={32} className="text-stone-300 mx-auto mb-3" />
                            <p className="text-stone-500 font-body">Written content coming soon</p>
                            <p className="text-stone-400 font-body text-sm mt-1">
                              Check back later for detailed guidance and instructions for this day.
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* The Experience Guide (id 100) */}
                  {selectedDay.id === 100 && (
                    <div>
                      <div className="p-6 md:p-8">
                        {/* Header */}
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">The Experience Guide</h3>
                          <p className="text-terracotta font-body text-lg italic mb-4">What to expect on this journey</p>
                        </div>

                        {/* Introduction */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Throughout this fascial reset journey, you may experience many changes — physically, mentally, and emotionally. These shifts are a sign that your body is healing, releasing stored tension, and recalibrating. It isn't always comfortable in the middle of it, but with consistency, trust, and perseverance, most people emerge feeling lighter, more energized, and more connected to themselves.
                          </p>
                          <p className="text-forest font-body font-medium mt-4 text-center italic">
                            Any change you notice during this process is normal. Trust the journey.
                          </p>
                        </div>

                        {/* What to Expect: Physically */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta/10 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">What to Expect: Physically</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              As your body goes through this experience, most people begin a healing cycle that can express itself in many ways. With time, fascial work unwinds harmful stress, tension, and restrictions so the body can heal itself.
                            </p>

                            <div className="grid grid-cols-3 gap-3 mb-6">
                              {[
                                'Feeling good & bad', 'Happy & sad', 'Light & heavy',
                                'Open & closed', 'Hot & cold', 'Nauseous & sweaty'
                              ].map((item) => (
                                <div key={item} className="bg-stone-50 rounded-lg p-3 text-center">
                                  <span className="text-stone-600 font-body text-sm">{item}</span>
                                </div>
                              ))}
                            </div>

                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              The direction it goes depends on your starting point and what your body needs at the time. It's all a normal part of the healing process — and the list of possible experiences is endless.
                            </p>

                            <div className="bg-terracotta/10 rounded-xl p-4 mb-4">
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Most of us have pushed our bodies hard over the years. Some of that stored tension has to come out, and it's not always comfortable. But soon, it will stabilize — and you'll feel better than ever.
                              </p>
                            </div>

                            <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-200">
                              <p className="text-forest font-body italic">
                                Trust your body. Trust this process. Your body is coming back into balance.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* What to Expect: Mentally */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-violet-50 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">What to Expect: Mentally</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              Through fascial work, the mind calms down. It becomes easier to focus and see solutions to problems that were invisible before. When the body is noisy, the brain is busy, distracted, and slow to process information. This is one of the most significant benefits of this work.
                            </p>

                            <div className="bg-forest/5 rounded-xl p-5 mb-6">
                              <p className="text-forest font-body font-medium mb-3">Be mindful throughout this process:</p>
                              <div className="space-y-3">
                                {[
                                  'When emotions are high, thoughts go up and down — positive to negative — creating stories and running old programs along the way.',
                                  'Be mindful of the narratives you tell yourself.',
                                  'Be mindful of the narratives you tell others.',
                                  'What stories are you going to tell to craft your identity?',
                                  'What words are you going to choose to describe your experience?',
                                ].map((item, idx) => (
                                  <div key={idx} className="flex gap-3">
                                    <span className="w-2 h-2 bg-violet-400 rounded-full mt-2 flex-shrink-0" />
                                    <p className="text-stone-600 font-body text-sm">{item}</p>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-stone-50 rounded-xl p-4 text-center">
                                <p className="text-stone-500 font-body text-sm mb-1">Are you sick?</p>
                                <p className="text-stone-500 font-body text-sm">Are you detoxing?</p>
                              </div>
                              <div className="bg-terracotta/5 rounded-xl p-4 text-center border border-terracotta/20">
                                <p className="text-forest font-heading text-lg">Or are you healing?</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* What to Expect: Emotionally */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-rose-50 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">What to Expect: Emotionally</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              A lot of people are not in tune with their emotions. For some, emotions remain a mystery, barely acknowledged or understood. For others, emotions are overwhelming forces, setting them off balance every time they surface. This often leads to a cycle of emotional suppression or overreaction.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed mb-6">
                              Fascial work helps people connect to and stabilize their emotions in real time.
                            </p>

                            <div className="space-y-4 mb-6">
                              {[
                                { left: 'This can feel intense, as it often brings forth feelings that have been buried deeply within', right: 'During this process, it\'s normal for emotions and long-held trauma to surface' },
                                { left: 'And builds emotional self-awareness', right: 'But it signifies that healing is just around the corner' },
                                { left: 'People develop a deeper understanding of the situations that stimulate their emotions', right: 'Now with the ability to recognize emotions in real time' },
                              ].map((pair, idx) => (
                                <div key={idx} className="grid md:grid-cols-2 gap-3">
                                  <div className="bg-stone-50 rounded-lg p-3 border-l-4 border-stone-300">
                                    <p className="text-stone-600 font-body text-sm">{pair.left}</p>
                                  </div>
                                  <div className="bg-terracotta/5 rounded-lg p-3 border-l-4 border-terracotta">
                                    <p className="text-stone-600 font-body text-sm">{pair.right}</p>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="bg-emerald-50 rounded-xl p-5 text-center border border-emerald-200">
                              <p className="text-stone-500 font-body text-sm mb-2">This doesn't mean you won't experience intense emotions ever again...</p>
                              <p className="text-forest font-heading text-lg">But now you will have the tools to handle them.</p>
                            </div>
                          </div>
                        </div>

                        {/* What to Do If Things Get Uncomfortable */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-amber-50 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">What to Do If Things Get Uncomfortable</h4>
                          </div>
                          <div className="p-6">
                            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                              {[
                                'Pause for a moment, sit, rest and relax',
                                'Share with the community',
                                'Attend a weekly meetup',
                                'Ask questions to the community',
                                'Hydrate with minerals, silica and structured water',
                                'Observe, journal and self-reflect on the experience',
                                'Continue the practice and let your body do what it needs to do',
                                'Organ reset lying in bed',
                                'Magnesium flake bath, epsom salt bath or ocean dip',
                                'Seated Fascial Maneuvers',
                                'High dose PowerCurc30',
                                'Trust the process',
                              ].map((item, idx) => (
                                <div key={idx} className="bg-stone-50 rounded-lg p-3 border border-stone-200 hover:bg-stone-100 transition-colors">
                                  <p className="text-stone-600 font-body text-sm">{item}</p>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 bg-terracotta/10 rounded-lg p-4 text-center">
                              <p className="text-stone-600 font-body text-sm italic">
                                Re-read this Experience Guide anytime you need a gentle reminder.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Closing */}
                        <div className="text-center py-4">
                          <p className="font-heading text-2xl text-forest mb-2">You are exactly where you need to be.</p>
                          <p className="text-stone-500 font-body">Trust the process. Trust your body.</p>
                          <span className="text-3xl mt-2 block">🌿</span>
                        </div>

                        {/* Full Guide Link */}
                        <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            I'll be breaking this down over the coming days, but if you're curious and want to dive deeper, have a look at the full guide:
                          </p>
                          <a
                            href="https://db.humangarage.info/storage/v1/object/public/content/guides/Fascial-Maneuvers-Guide-(English).pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors"
                            data-testid="fascial-guide-link"
                          >
                            <FileText size={18} />
                            Full Fascial Maneuvers Guide
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Fascial Facial Maneuvers (id 101) */}
                  {selectedDay.id === 101 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Fascial Facial Maneuvers</h3>
                          <p className="text-stone-500 font-body italic text-lg">Energy and Release</p>
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
                          <div className="flex items-start gap-3">
                            <div>
                              <p className="text-stone-700 font-body leading-relaxed">
                                <strong>Short Fascial Facial Maneuvers</strong> can help if you're feeling pressure or headache post-practice, as we are moving stuck energy from the feet upward. It can get stuck in the head, so to speak. Use these techniques to help move that energy through.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Energy and Release</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              As the body begins to reorganize and muscles return to better timing, it's common to feel subtle sensations moving through the body. Some people describe this as warmth, tingling, pulsing, or a gentle wave of energy rising upward through the spine and torso.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              This isn't unusual. When areas of tension or long-held protective patterns begin to release, the nervous system and surrounding tissues start communicating more freely. Blood flow improves, breathing deepens, and the body's internal signaling systems become more active. What once felt stuck or quiet can begin to move again.
                            </p>
                            <div className="bg-forest/5 rounded-xl p-5 text-center">
                              <p className="text-forest font-body leading-relaxed italic">
                                Allow these sensations to pass without trying to control them. Simply noticing and breathing through the experience is often enough. The body has a remarkable ability to restore balance when given the space and attention to do so.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 1 – Regulation First (id 3) */}
                  {selectedDay.id === 3 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900 flex items-center justify-center">
                        {selectedDay.videoUrl ? (
                          <iframe
                            src={selectedDay.videoUrl}
                            title={selectedDay.title}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <div className="text-center">
                            <div className="w-20 h-20 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Video size={32} className="text-stone-600" />
                            </div>
                            <p className="text-stone-500 font-body">Video coming soon</p>
                          </div>
                        )}
                      </div>

                      <div className="p-6 md:p-8">
                        {/* Welcome Header */}
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Regulation First</h3>
                          <p className="text-terracotta font-body text-lg italic mb-4">Welcome to your first reset.</p>
                          <p className="text-stone-600 font-body leading-relaxed max-w-2xl mx-auto">
                            Today is about one thing: <span className="font-medium text-forest">regulation</span>.
                          </p>
                        </div>

                        {/* Before/After emphasis */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8 text-center">
                          <div className="space-y-1 text-stone-500 font-body mb-3">
                            <p>Before strength.</p>
                            <p>Before flexibility.</p>
                            <p>Before transformation.</p>
                          </div>
                          <p className="font-heading text-2xl text-forest">Regulation.</p>
                        </div>

                        {/* Nervous System */}
                        <div className="mb-8">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            Your nervous system sets the tone for everything. When it feels safe, the body releases. When it feels threatened, it holds. Fascia listens closely to this conversation.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            That's why these sessions are short.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed">
                            15–20 minutes may not look dramatic on paper. But <span className="font-medium text-forest">consistency is quietly powerful</span>. Small daily inputs signal safety. Safety signals release. Release creates space.
                          </p>
                        </div>

                        <div className="bg-terracotta/10 rounded-xl p-6 mb-8 text-center">
                          <p className="text-forest font-body italic leading-relaxed">
                            We are not forcing change.<br />We are creating conditions for it.
                          </p>
                        </div>

                        {/* Why Short Sessions Work */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta/10 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">Why Short Sessions Work</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              Long, intense sessions can spike stress chemistry.
                            </p>
                            <p className="text-stone-600 font-body font-medium mb-3">Short sessions:</p>
                            <ul className="space-y-2 mb-6">
                              {['Are sustainable', 'Support nervous system stability', 'Build daily momentum', 'Create measurable change over time'].map((item, idx) => (
                                <li key={idx} className="flex gap-3 text-stone-600 font-body">
                                  <span className="w-2 h-2 bg-terracotta rounded-full mt-2 flex-shrink-0" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                            <p className="text-stone-600 font-body leading-relaxed mb-2">
                              You are building a practice you can maintain beyond these 28 days.
                            </p>
                            <p className="text-forest font-body italic">Think gentle rewiring, not overhaul.</p>
                          </div>
                        </div>

                        {/* Hydration + Mineral Support */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-blue-50 px-6 py-4 border-b border-stone-200">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-heading text-lg">
                                <Droplet size={20} />
                              </div>
                              <h4 className="font-heading text-xl text-forest">Hydration + Mineral Support</h4>
                            </div>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              As fascia unwinds, the body may release stored waste products and inflammatory byproducts. Supporting elimination matters.
                            </p>
                            <p className="text-forest font-body font-medium mb-3">Today, begin with:</p>
                            
                            <div className="bg-stone-50 rounded-xl p-5 mb-4">
                              <p className="font-heading text-forest mb-3">Hydration</p>
                              <ul className="space-y-2">
                                {[
                                  'Increase clean water intake',
                                  'Add a pinch of high-quality mineral salt if appropriate',
                                  'Consider electrolytes if you run low energy'
                                ].map((item, idx) => (
                                  <li key={idx} className="flex gap-3 text-stone-600 font-body text-sm">
                                    <span className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 flex-shrink-0" />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="bg-terracotta/10 rounded-lg p-4">
                              <p className="text-forest font-body italic text-sm">
                                Fascia is highly responsive to hydration. Think fluid matrix, not dry rope.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Mineral + Detox Support */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-emerald-50 px-6 py-4 border-b border-stone-200">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-heading text-lg">
                                <Sparkles size={20} />
                              </div>
                              <div>
                                <h4 className="font-heading text-xl text-forest">Mineral + Detox Support</h4>
                                <span className="text-emerald-600 font-body text-sm">Optional</span>
                              </div>
                            </div>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-500 font-body italic text-sm mb-4">
                              Everything below is optional. Listen to your body.
                            </p>
                            <p className="text-stone-600 font-body mb-3">You may consider:</p>
                            <div className="grid md:grid-cols-2 gap-3 mb-6">
                              {[
                                { name: 'Sea moss', desc: 'mineral-rich support' },
                                { name: 'Silica', desc: 'connective tissue & structural support' },
                                { name: 'Mineral blends', desc: 'replenishment during detox' },
                                { name: 'Simple detox teas', desc: 'dandelion, nettle, ginger, etc.' },
                              ].map((item, idx) => (
                                <div key={idx} className="bg-stone-50 rounded-lg p-3">
                                  <span className="font-body font-medium text-forest text-sm">{item.name}</span>
                                  <span className="text-stone-500 font-body text-sm"> — {item.desc}</span>
                                </div>
                              ))}
                            </div>

                            <p className="text-stone-600 font-body mb-3">If you are experienced with binders, options may include:</p>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {['Chlorella', 'Bentonite clay', 'Food-grade diatomaceous earth', 'Silica-based binders'].map((item) => (
                                <span key={item} className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-body border border-emerald-200">
                                  {item}
                                </span>
                              ))}
                            </div>
                            <p className="text-stone-500 font-body text-sm mb-3">
                              Binders help escort released toxins out of the system rather than allowing recirculation.
                            </p>

                            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                              <p className="text-amber-800 font-body text-sm font-medium mb-2">If using binders:</p>
                              <ul className="space-y-1">
                                {['Take away from food and supplements', 'Increase hydration', 'Start low and assess tolerance'].map((item, idx) => (
                                  <li key={idx} className="flex gap-2 text-amber-700 font-body text-sm">
                                    <span className="text-amber-500">•</span>{item}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <p className="text-forest font-body italic text-sm mt-4">
                              If you're unsure, keep it simple. Hydration alone is powerful.
                            </p>
                          </div>
                        </div>

                        {/* What You Might Notice */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-violet-50 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">What You Might Notice</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              Day 1 may feel subtle.
                            </p>
                            <p className="text-stone-600 font-body mb-3">Or you may feel:</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                              {['Softer', 'More grounded', 'Slightly tired', 'Slightly energized', 'Emotional', 'Clear'].map((item) => (
                                <div key={item} className="bg-stone-50 rounded-lg p-3 text-center">
                                  <span className="text-stone-600 font-body text-sm">{item}</span>
                                </div>
                              ))}
                            </div>
                            <p className="text-forest font-body italic text-center">All normal. Your body is recalibrating.</p>
                          </div>
                        </div>

                        {/* Today's Invitation */}
                        <div className="mb-8 bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-white">
                          <h4 className="font-heading text-xl mb-6 text-terracotta">Today's Invitation</h4>
                          <div className="space-y-4 text-cream/90 font-body">
                            <p>Complete your Stress Reset video.</p>
                            <p>Drink water.</p>
                            <p>Move gently through your day.</p>
                            <p>Notice small shifts.</p>
                          </div>
                        </div>

                        {/* Closing */}
                        <div className="text-center py-4">
                          <div className="space-y-1 text-stone-500 font-body mb-4">
                            <p>We are not chasing intensity.</p>
                            <p>We are building capacity.</p>
                          </div>
                          <div className="space-y-1 text-forest font-body font-medium mb-6">
                            <p>Quiet inputs.</p>
                            <p>Consistent rhythm.</p>
                            <p>Nervous system first.</p>
                          </div>
                          <p className="font-heading text-2xl text-forest">Welcome to the work.</p>
                          <span className="text-3xl mt-2 block">🌿</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 2 – Change Is Inevitable (id 4) */}
                  {selectedDay.id === 4 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        {/* Welcome Header */}
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Change Is Inevitable</h3>
                          <p className="text-terracotta font-body text-lg italic mb-4">Let's talk about change.</p>
                          <p className="text-stone-600 font-body leading-relaxed max-w-2xl mx-auto">
                            Change is happening whether we participate or not.
                          </p>
                        </div>

                        {/* Dynamic Nature */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <div className="space-y-1 text-stone-600 font-body text-center leading-relaxed">
                            <p>Cells turn over. Fascia remodels. Habits reinforce.</p>
                            <p>Posture adapts. Thoughts loop. Hormones shift.</p>
                            <p>Seasons rotate. You are not static.</p>
                          </div>
                          <p className="font-heading text-xl text-forest text-center mt-4">
                            You are dynamic tissue, dynamic chemistry, dynamic consciousness.
                          </p>
                        </div>

                        <div className="text-center mb-8">
                          <p className="text-stone-500 font-body mb-2">The question is not <span className="italic">if</span> you will change.</p>
                          <p className="text-forest font-heading text-xl">The question is <span className="text-terracotta">how</span>.</p>
                        </div>

                        {/* Change Happens Either Way */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta/10 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">Change Happens Either Way</h4>
                          </div>
                          <div className="p-6">
                            <div className="grid md:grid-cols-2 gap-4 mb-6">
                              <div className="bg-stone-50 rounded-xl p-4 border-l-4 border-stone-300">
                                <p className="text-stone-600 font-body text-sm">If we repeat <span className="font-medium">tension</span>, we change in that direction.</p>
                              </div>
                              <div className="bg-terracotta/5 rounded-xl p-4 border-l-4 border-terracotta">
                                <p className="text-stone-600 font-body text-sm">If we repeat <span className="font-medium text-forest">regulation</span>, we change in that direction.</p>
                              </div>
                            </div>
                            <p className="text-forest font-heading text-lg text-center mb-4">Repetition is the architect.</p>
                            <div className="bg-terracotta/10 rounded-xl p-5">
                              <p className="text-stone-600 font-body leading-relaxed mb-3">
                                Twenty minutes a day may not feel dramatic, but repetition whispers to your system:
                              </p>
                              <p className="text-forest font-heading text-xl italic text-center">"We are doing this now."</p>
                              <p className="text-stone-500 font-body text-center mt-2">And the body listens.</p>
                            </div>
                          </div>
                        </div>

                        {/* Why Conscious Change Feels Hard */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-violet-50 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">Why Conscious Change Feels Hard</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              When you consciously choose change, it can feel uncomfortable.
                            </p>
                            <div className="bg-stone-50 rounded-lg p-4 mb-4">
                              <p className="text-stone-500 font-body text-sm">Not because it's wrong.</p>
                              <p className="text-forest font-body font-medium text-sm">But because your system doesn't really want change.</p>
                            </div>

                            <div className="space-y-2 mb-6 text-stone-600 font-body">
                              <p>Your nervous system prefers <span className="font-medium">familiar</span> over optimal.</p>
                              <p>Familiar tension feels safer than unfamiliar freedom.</p>
                              <p>Familiar tightness feels predictable.</p>
                            </div>

                            <p className="text-stone-600 font-body mb-3">So when you interrupt old patterns, there may be resistance:</p>
                            <div className="grid grid-cols-2 gap-3 mb-6">
                              {['Skipping the practice', 'Feeling restless', 'Feeling emotional', 'Negotiating with yourself'].map((item) => (
                                <div key={item} className="bg-stone-50 rounded-lg p-3 text-center">
                                  <span className="text-stone-600 font-body text-sm">{item}</span>
                                </div>
                              ))}
                            </div>

                            <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-200">
                              <p className="text-stone-500 font-body mb-1">That's not failure.</p>
                              <p className="text-emerald-700 font-heading text-lg">That's rewiring.</p>
                            </div>
                          </div>
                        </div>

                        {/* You Are Changing Either Way */}
                        <div className="mb-8 bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-white">
                          <h4 className="font-heading text-xl mb-6 text-terracotta">You Are Changing Either Way</h4>
                          <div className="space-y-4 mb-6">
                            <p className="text-cream/90 font-body">If you do nothing, you change.</p>
                            <p className="text-cream/90 font-body">If you show up for 15–20 minutes daily, you change.</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-white/10 rounded-xl p-4 text-center">
                              <p className="text-cream/70 font-body text-sm">One path reinforces</p>
                              <p className="text-cream font-heading">old patterns</p>
                            </div>
                            <div className="bg-white/10 rounded-xl p-4 text-center">
                              <p className="text-cream/70 font-body text-sm">One path introduces</p>
                              <p className="text-terracotta font-heading">new ones</p>
                            </div>
                          </div>
                          <p className="text-cream font-body text-center italic">Let's see what happens.</p>
                        </div>

                        {/* Closing */}
                        <div className="text-center py-4">
                          <div className="space-y-1 text-stone-500 font-body mb-4">
                            <p>Not with force.</p>
                            <p>Not with drama.</p>
                            <p className="text-forest font-medium">With repetition.</p>
                          </div>
                          <div className="bg-terracotta/10 rounded-xl p-6 mt-6 mb-6">
                            <div className="space-y-2 text-stone-600 font-body">
                              <p>Complete today's reset.</p>
                              <p>Hydrate.</p>
                              <p>Notice.</p>
                            </div>
                          </div>
                          <p className="font-heading text-2xl text-forest mb-2">Change is inevitable.</p>
                          <p className="text-stone-600 font-body">You are simply choosing the direction.</p>
                          <span className="text-3xl mt-2 block">🌿</span>
                        </div>

                        {/* Beautiful Dancing Fascia */}
                        <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center mb-8">
                          <p className="font-heading text-lg text-forest mb-3">Beautiful Dancing Fascia</p>
                          <a
                            href="https://www.facebook.com/share/r/14eryX99kDE/?mibextid=wwXIfr"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-terracotta text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors"
                            data-testid="dancing-fascia-link"
                          >
                            <Play size={18} />
                            Watch on Facebook
                          </a>
                        </div>

                        {/* 7 Step Approach */}
                        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest/5 px-6 py-5 border-b border-stone-200 text-center">
                            <h4 className="font-heading text-2xl text-forest">7 Step Approach</h4>
                            <p className="font-heading text-lg text-terracotta">to Healing Yourself</p>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-6">
                              We see the body as a fluid-adaptive, biological computing system that holds chemicals, emotions, memory and trauma. When we introduce chemicals, lack movement, store emotions and repeat patterns over and over again the body begins to break down as a result. Below you will find a 7 step approach to healing yourself.
                            </p>

                            <div className="space-y-6">
                              {[
                                {
                                  num: 1,
                                  title: 'Remove Chemicals from Your Environment',
                                  text: 'Most people are over consuming chemicals in their daily life from the food they eat to the cleaning, care and beauty products. These chemicals are causing a wide range of issues including depression, hormonal imbalances, digestive issues, and autoimmune. By clearing your environment from chemicals you will reduce the unnecessary stress and load on your body so it has the time and space to heal itself.'
                                },
                                {
                                  num: 2,
                                  title: 'Clear the Digestive System',
                                  text: 'Over 90% of people we work with today have some sort of digestive issue from blocked flow to gas, bloating, and distention. This is due to harmful pesticides, herbicides and GMOs that are found in most food today. We want to focus on clearing the clutter otherwise undigested food sits in the gut and wreaks havoc.'
                                },
                                {
                                  num: 3,
                                  title: 'Reduce Inflammation',
                                  text: 'If you have achy, painful joints, or swelling and inflammation in your body, that means things aren\'t flowing properly. When things don\'t flow properly, toxins build, mucus and fat form and the body begins to deteriorate.'
                                },
                                {
                                  num: 4,
                                  title: 'Remineralize & Replenish Silica',
                                  text: 'Most food and water lack the vital minerals and nutrients that we need in order to function. The number one cause of dehydration is the overconsumption of demineralized water. With toxic chemical consumption we deplete the body of silica which is fundamental for minerals to be absorbed. It also plays a vital role in rebuilding all skin, bones, and tissue in the body.'
                                },
                                {
                                  num: 5,
                                  title: 'Emotional Awareness',
                                  text: 'When emotions land in the body, the body contracts. When these emotions aren\'t recognized or they are acted upon in the wrong way, it creates dysfunction in our bodies and lives. When we don\'t express ourselves, this also holds the emotions in our body.'
                                },
                                {
                                  num: 6,
                                  title: 'Break Harmful Habits & Programs',
                                  text: 'Everyone has repeating habits and programs that run and govern their life. If you can recognize the harmful patterns and actively change them, then your life changes. Otherwise you spiral into a constant ground hog day of experiences that never seem to end.'
                                },
                                {
                                  num: 7,
                                  title: 'Move Your Fascia',
                                  text: 'Fascia holds memory, emotions, trauma and chemicals. It holds your muscles, bones, tendons, ligaments and organs. When there is a restriction in your fascia there is a restriction in everything it touches. By implementing the Fascial Maneuvers into your daily practice you can unwind tension in your fascia to restore flow, reduce stress and repair the body.'
                                },
                              ].map((step) => (
                                <div key={step.num} className="flex gap-4">
                                  <div className="w-10 h-10 bg-forest rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                    <span className="text-cream font-heading text-lg">{step.num}</span>
                                  </div>
                                  <div>
                                    <h5 className="font-heading text-lg text-forest mb-1">{step.title}</h5>
                                    <p className="text-stone-600 font-body text-sm leading-relaxed">{step.text}</p>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="mt-6 bg-amber-50 rounded-xl p-4 border border-amber-200">
                              <p className="text-amber-800 font-body text-sm italic leading-relaxed">
                                Please note: although this is a supplement guide, one MUST also follow the other steps otherwise it won't work. This is an active process of learning about your body and taking care of yourself. It's a lifestyle. This is not just another pop a pill quick fix solution.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 3 – Intention (id 5) */}
                  {selectedDay.id === 5 && (
                    <div>
                      {/* Video placeholder */}
                      <div className="aspect-video bg-stone-900 flex items-center justify-center">
                        {selectedDay.videoUrl ? (
                          <iframe
                            src={selectedDay.videoUrl}
                            title={selectedDay.title}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <div className="text-center">
                            <div className="w-20 h-20 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Video size={32} className="text-stone-600" />
                            </div>
                            <p className="text-stone-500 font-body">Video coming soon</p>
                          </div>
                        )}
                      </div>

                      <div className="p-6 md:p-8">
                        {/* Header */}
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Intention</h3>
                          <p className="text-stone-600 font-body leading-relaxed max-w-2xl mx-auto">
                            One of the things I see missing for many people right now is not effort.
                          </p>
                          <p className="font-heading text-2xl text-terracotta mt-3">It's intention.</p>
                        </div>

                        {/* The Busy List */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8 text-center">
                          <div className="space-y-1 text-stone-600 font-body">
                            <p>We are busy.</p>
                            <p>We are trying.</p>
                            <p>We are adding things in.</p>
                            <p>We are keeping up.</p>
                            <p>We are improving, optimizing, surviving, juggling.</p>
                          </div>
                          <p className="text-forest font-body font-medium mt-4">
                            But sometimes what's missing is the primary intention underneath it all.
                          </p>
                        </div>

                        <div className="mb-8">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            Without that, we can end up scattering ourselves. Wellness over here. Career over there. Relationships somewhere else. Energy going in ten directions at once.
                          </p>
                          <p className="text-stone-500 font-body italic text-center">And then we wonder why we feel thin.</p>
                        </div>

                        {/* Intention Is Different */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta/10 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">Intention Is Different</h4>
                          </div>
                          <div className="p-6">
                            <div className="space-y-2 mb-6 text-stone-500 font-body">
                              <p>It's different than goals.</p>
                              <p>It's different than productivity.</p>
                              <p>It's different than pleasing.</p>
                            </div>
                            <p className="text-forest font-heading text-xl text-center mb-6">Intention is directional.</p>

                            <div className="bg-stone-50 rounded-xl p-5">
                              <p className="text-stone-600 font-body leading-relaxed mb-3">
                                If you open Google Maps but don't drop a destination, you just see roads. Fields. Options. Movement without orientation.
                              </p>
                              <p className="text-stone-600 font-body leading-relaxed mb-3">
                                The moment you drop in a location, the map reorganizes. Routes appear. Traffic adjusts. Direction clarifies.
                              </p>
                              <p className="text-forest font-body font-medium">We are much the same.</p>
                            </div>
                          </div>
                        </div>

                        <div className="mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            When we choose a clear, honest intention, our nervous system, our attention, and our energy begin to organize around it.
                          </p>
                        </div>

                        {/* The Intention Must Be Yours */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-violet-50 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">The Intention Must Be Yours</h4>
                          </div>
                          <div className="p-6">
                            <div className="space-y-2 mb-6">
                              {[
                                'Not the one that pleases your partner.',
                                'Not the one that makes your family comfortable.',
                                'Not the one that looks impressive.',
                                'Not the one society rewards.',
                              ].map((item, idx) => (
                                <div key={idx} className="flex gap-3">
                                  <span className="w-2 h-2 bg-stone-300 rounded-full mt-2 flex-shrink-0" />
                                  <p className="text-stone-500 font-body">{item}</p>
                                </div>
                              ))}
                            </div>
                            <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                              <p className="text-forest font-heading text-lg">It has to be rooted in your highest good.</p>
                            </div>
                          </div>
                        </div>

                        {/* Example Intentions */}
                        <div className="mb-8">
                          <p className="text-stone-600 font-body mb-4">In this reset, that may sound like:</p>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {[
                              'I intend to build a resilient body.',
                              'I intend to regulate my nervous system.',
                              'I intend to feel at home in myself.',
                              'I intend to move through life with more steadiness.',
                            ].map((item, idx) => (
                              <div key={idx} className="bg-forest/5 rounded-xl p-4 border-l-4 border-forest">
                                <p className="text-forest font-body italic text-sm">"{item}"</p>
                              </div>
                            ))}
                          </div>
                          <p className="text-center text-stone-500 font-body mt-4">Simple. Clear. True.</p>
                        </div>

                        <div className="mb-8">
                          <p className="text-stone-600 font-body leading-relaxed mb-3">
                            When the intention is authentic, your system starts to coordinate around it. Like spokes moving toward the center of a wheel, scattered energy begins to align.
                          </p>
                          <div className="bg-stone-50 rounded-lg p-4 text-center">
                            <p className="text-stone-500 font-body text-sm">This is not a quick fix.</p>
                            <p className="text-stone-500 font-body text-sm">It is not a 24-hour shift.</p>
                            <p className="text-forest font-body font-medium mt-2">It is a long-term orientation. An energetic reorganization.</p>
                          </div>
                        </div>

                        {/* Today's Invitation */}
                        <div className="mb-8 bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-white">
                          <h4 className="font-heading text-xl mb-6 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body mb-4">Take a few minutes and ask:</p>
                          <div className="space-y-3 mb-6">
                            <div className="bg-white/10 rounded-xl p-4">
                              <p className="text-cream font-body italic">What is my primary intention for this season of my life?</p>
                            </div>
                            <div className="bg-white/10 rounded-xl p-4">
                              <p className="text-cream font-body italic">And does my current energy expenditure reflect that?</p>
                            </div>
                          </div>
                          <div className="space-y-1 text-cream/80 font-body text-center">
                            <p>Set it quietly.</p>
                            <p>Let it settle.</p>
                            <p>Then give your system time to organize around it.</p>
                          </div>
                        </div>

                        {/* Closing */}
                        <div className="text-center py-4">
                          <div className="space-y-1 text-stone-600 font-body mb-4">
                            <p>The body listens.</p>
                            <p>The nervous system adapts.</p>
                            <p>Energy follows direction.</p>
                          </div>
                          <p className="font-heading text-2xl text-forest">And direction begins with intention.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 4 Content - Organ Reset (id 6) */}
                  {selectedDay.id === 6 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        {/* Header */}
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">The Organ Reset</h3>
                          <p className="text-stone-500 font-body italic text-lg">A gentle foundational practice within Fascial Maneuvers</p>
                        </div>

                        {/* Main Description */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            The Organ Reset is a gentle foundational practice within Fascial Maneuvers designed to support internal balance and nervous system regulation. Through simple positioning and intentional breath, the body is given space to decompress, hydrate tissue, and restore communication between organs and structure.
                          </p>
                        </div>

                        {/* The Subtle Power */}
                        <div className="mb-8 text-center">
                          <div className="space-y-1 text-stone-500 font-body italic mb-4">
                            <p>It looks subtle. It feels simple.</p>
                          </div>
                          <p className="font-heading text-2xl text-terracotta">But it creates profound internal recalibration.</p>
                        </div>

                        {/* Benefits */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta/10 px-6 py-4 border-b border-stone-200">
                            <h4 className="font-heading text-xl text-forest">What Consistent Practice Supports</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body text-sm leading-relaxed mb-4">
                              When practiced consistently, the Organ Reset can support:
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3">
                              {[
                                'Digestion',
                                'Circulation',
                                'Emotional steadiness',
                                'Overall systemic balance',
                              ].map((benefit, idx) => (
                                <div key={idx} className="flex gap-2 items-center bg-emerald-50 rounded-lg p-3">
                                  <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0" />
                                  <p className="text-forest font-body text-sm font-medium">{benefit}</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-stone-600 font-body text-sm mt-4 text-center italic">
                              It is one of the most accessible and powerful starting points in the fascial maneuver system.
                            </p>
                          </div>
                        </div>

                        {/* Organs We Work With */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-forest/20 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Organs We Work With</h4>
                          </div>
                          <div className="p-6">
                            <div className="flex flex-wrap gap-3 justify-center">
                              {[
                                'Ileocecal Valve',
                                'Valve of Houston',
                                'Stomach / Small Intestine',
                                'Liver / Gall Bladder',
                                'Kidneys',
                                'Spleen',
                                'Bladder',
                                'Thyroid',
                              ].map((organ, idx) => (
                                <span key={idx} className="bg-forest/5 text-forest px-4 py-2 rounded-full font-body text-sm border border-forest/15 font-medium">
                                  {organ}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Today's Invitation */}
                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center mb-8">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed">
                            Follow the video above and allow each position to settle. There's no rushing here — let your body receive the work, one organ at a time.
                          </p>
                        </div>

                        {/* Wellness Article Link */}
                        <div className="bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Wellness Article</h4>
                          </div>
                          <div className="p-6">
                            <p className="font-heading text-xl text-forest mb-3">
                              Did You Know Your Body Is a Pump?
                            </p>
                            <p className="text-stone-600 font-body text-sm leading-relaxed mb-5">
                              Our bodies and organs are pumps — and activating them is essential to our overall wellness. If you're feeling sluggish and not sure where to start, the Organ Reset can really begin to get things moving towards a natural motivation for more movement.
                            </p>
                            <a
                              href="https://gwwithjill.com/resources/movement?resource=body-is-a-pump"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block bg-terracotta text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors"
                            >
                              Read the Article &rarr;
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 5 Content - Stress Reset (id 7) */}
                  {selectedDay.id === 7 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Stress Reset</h3>
                          <p className="text-stone-500 font-body italic text-lg">NO anti-gravity</p>
                        </div>

                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Today's practice focuses on resetting the stress response in the body. These are grounding, floor-based maneuvers — no anti-gravity work. Allow yourself to sink into each position and let the body unwind.
                          </p>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Today's Maneuvers</h4>
                          </div>
                          <div className="p-6">
                            <div className="grid sm:grid-cols-2 gap-4">
                              {[
                                { name: 'Palate Swipe', desc: 'Releasing tension held in the roof of the mouth and its connection to the cranial system' },
                                { name: 'Totally Twisted', desc: 'A full-body rotational release to unwind deep holding patterns' },
                                { name: 'Psoas Release', desc: 'Letting go of the body\'s primary stress muscle — the deep hip flexor' },
                                { name: 'Jaw Release', desc: 'Releasing the jaw and its powerful connection to the nervous system' },
                              ].map((item, idx) => (
                                <div key={idx} className="bg-forest/5 rounded-xl p-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{idx + 1}</span>
                                    <p className="font-body font-medium text-forest text-sm">{item.name}</p>
                                  </div>
                                  <p className="text-stone-500 font-body text-xs leading-relaxed">{item.desc}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed">
                            Follow the video and allow each maneuver to settle. There is no rushing — let your nervous system receive the reset, one release at a time.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 6 Content - Beach Stress Reset (id 8) */}
                  {selectedDay.id === 8 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Beach Stress Reset</h3>
                          <p className="text-stone-500 font-body italic text-lg">Day 6 — Checking In</p>
                        </div>

                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Of the <strong>7-Step Approach</strong> outlined in Day 2, here's where we are this week:
                          </p>
                        </div>

                        {/* Active This Week */}
                        <div className="mb-6 bg-white rounded-2xl border-2 border-emerald-200 overflow-hidden">
                          <div className="bg-emerald-500 px-6 py-3">
                            <h4 className="font-heading text-base text-white">Actively Focusing On This Week</h4>
                          </div>
                          <div className="p-5 space-y-3">
                            {[
                              { num: 7, title: 'Move Your Fascia' },
                              { num: 6, title: 'Break Harmful Habits & Programs' },
                              { num: 4, title: 'Remineralize & Replenish Silica' },
                            ].map((step) => (
                              <div key={step.num} className="flex items-center gap-3 bg-emerald-50 rounded-xl p-3">
                                <span className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-heading flex-shrink-0">{step.num}</span>
                                <p className="font-body text-forest text-sm font-medium">{step.title}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Quietly Addressing */}
                        <div className="mb-6 bg-white rounded-2xl border border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta/10 px-6 py-3 border-b border-terracotta/20">
                            <h4 className="font-heading text-base text-forest">Addressing Quietly</h4>
                          </div>
                          <div className="p-5">
                            <div className="flex items-center gap-3 bg-terracotta/5 rounded-xl p-3">
                              <span className="bg-terracotta text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-heading flex-shrink-0">5</span>
                              <p className="font-body text-forest text-sm font-medium">Emotional Awareness</p>
                            </div>
                          </div>
                        </div>

                        {/* Background */}
                        <div className="mb-6 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-stone-100 px-6 py-3 border-b border-stone-200">
                            <h4 className="font-heading text-base text-forest">Happening in the Background</h4>
                          </div>
                          <div className="p-5">
                            <div className="flex items-center gap-3 bg-stone-50 rounded-xl p-3">
                              <span className="bg-stone-400 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-heading flex-shrink-0">3</span>
                              <p className="font-body text-forest text-sm font-medium">Reduce Inflammation</p>
                            </div>
                          </div>
                        </div>

                        {/* Coming Next Week */}
                        <div className="mb-8 bg-white rounded-2xl border border-forest/20 overflow-hidden">
                          <div className="bg-forest px-6 py-3">
                            <h4 className="font-heading text-base text-cream">Coming More Into Focus Next Week</h4>
                          </div>
                          <div className="p-5 space-y-3">
                            {[
                              { num: 1, title: 'Remove Chemicals from Your Environment' },
                              { num: 2, title: 'Clear the Digestive System' },
                              { num: 3, title: 'Reduce Inflammation' },
                            ].map((step) => (
                              <div key={step.num} className="flex items-center gap-3 bg-forest/5 rounded-xl p-3">
                                <span className="bg-forest text-cream w-8 h-8 rounded-full flex items-center justify-center text-sm font-heading flex-shrink-0">{step.num}</span>
                                <p className="font-body text-forest text-sm font-medium">{step.title}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed">
                            Follow the video and enjoy the beach stress reset. Notice how the body is already beginning to shift — we're layering the steps gently, and your system is responding.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 7 Content - Lying Down Organ Reset (id 9) */}
                  {selectedDay.id === 9 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Lying Down Organ Reset</h3>
                          <p className="text-stone-500 font-body italic text-lg">Organs & Stored Emotions</p>
                        </div>

                        {/* Organ Reset Image */}
                        <div className="mb-8 rounded-2xl overflow-hidden border border-stone-200">
                          <img
                            src="/1.png"
                            alt="Organ Reset anatomy diagram"
                            className="w-full object-contain"
                          />
                        </div>

                        {/* Main intro */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            Across many traditional healing systems, the organs have long been associated with emotional experience. In practices such as Traditional Chinese Medicine, Ayurveda, somatic therapy, and modern fascia research, the body is viewed not just as a mechanical structure, but as a living system that records and responds to life events.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            From this perspective, our organs are not only performing physical tasks like filtering blood, digesting food, or regulating hormones. They are also deeply connected to the nervous system and our emotional landscape. When experiences are stressful, overwhelming, or unresolved, the body sometimes adapts by holding tension patterns or protective responses within tissues and organ systems.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed">
                            Over time, these patterns may show up as subtle signals: digestive changes, breath restriction, fatigue, emotional reactivity, or areas of the body that feel consistently tight or sensitive.
                          </p>
                        </div>

                        <div className="mb-8 bg-terracotta/10 rounded-2xl p-6 text-center">
                          <p className="text-forest font-body leading-relaxed italic">
                            This idea of emotions being connected to organs may feel unfamiliar to those trained in strictly mechanical models of the body. Rather than viewing it as a fixed rule, it can be helpful to approach it as an additional lens — a way of listening to the body with curiosity.
                          </p>
                        </div>

                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            In this program, we gently explore how releasing fascial tension and restoring movement around the organs may also create space for emotional regulation, nervous system balance, and improved physical function.
                          </p>
                          <p className="text-forest font-body leading-relaxed font-medium text-center italic">
                            The body is an intelligent system. Sometimes when we create the right conditions, it begins to reorganize in ways that support both physical and emotional wellbeing.
                          </p>
                        </div>

                        {/* Emotion Images */}
                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                          <div className="rounded-2xl overflow-hidden border border-stone-200">
                            <img
                              src="/3.png"
                              alt="Emotions that affect your organs - negative patterns"
                              className="w-full object-contain"
                            />
                          </div>
                          <div className="rounded-2xl overflow-hidden border border-stone-200">
                            <img
                              src="/4.png"
                              alt="Emotions that support your organs - positive patterns"
                              className="w-full object-contain"
                            />
                          </div>
                        </div>

                        {/* Where These Ideas Come From */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Where These Ideas Come From</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              The idea that organs and emotions are connected is not new. It appears in many traditional healing systems and is now being explored again through modern somatic and nervous system research.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              In Traditional Chinese Medicine, for example, different organs are associated with particular emotional patterns. Ayurveda also recognizes relationships between emotional states, digestion, and organ function. More recently, fields like psychoneuroimmunology, somatic therapy, and fascia research are highlighting how closely our physical tissues, nervous system, and emotional experiences are intertwined.
                            </p>
                            <p className="text-forest font-body leading-relaxed italic">
                              From this perspective, the body is not simply mechanical. It is responsive, adaptive, and constantly communicating.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              When we experience stress, grief, anger, fear, or prolonged pressure, the body often creates protective patterns — changes in breath, muscle tension, circulation, and even organ motility. Over time, these patterns can become familiar holding places in the body.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              Exploring the relationship between organs and emotions offers one more lens through which we can listen to the body. Not as a rigid rule — but as an invitation to notice.
                            </p>
                          </div>
                        </div>

                        {/* Signals the Body May Show */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Signals the Body May Show</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              When an organ system is under strain, the signals are often subtle at first. They may show up as physical sensations, shifts in energy, or emotional patterns that repeat over time.
                            </p>
                            <p className="text-stone-600 font-body text-sm mb-4">Some common signals people notice include:</p>
                            <div className="grid sm:grid-cols-2 gap-3 mb-6">
                              {[
                                'Digestive changes or discomfort',
                                'Shallow breathing or tightness around the diaphragm',
                                'Fatigue that doesn\'t resolve with rest',
                                'Tension in specific areas of the body',
                                'Heightened emotional sensitivity or irritability',
                                'Recurring emotional themes linked with certain stressors',
                              ].map((signal, idx) => (
                                <div key={idx} className="flex gap-2 items-start bg-terracotta/5 rounded-lg p-3">
                                  <span className="w-2 h-2 bg-terracotta rounded-full mt-1.5 flex-shrink-0" />
                                  <p className="text-stone-600 font-body text-sm">{signal}</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              None of these signs point to a single cause. The body is complex, and many factors influence our health.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              But when we create conditions for the body to decompress — through breath, movement, fascia work, rest, hydration, and supportive practices — the system often begins to reorganize itself. And sometimes that reorganization includes both physical and emotional shifts.
                            </p>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed">
                            Approaching the body with curiosity rather than force allows these changes to unfold naturally. Follow the lying down organ reset video and notice what arises — physically and emotionally.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 8 Content - Stress Reset (id 10) */}
                  {selectedDay.id === 10 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Stress Reset</h3>
                          <p className="text-stone-500 font-body italic text-lg">16-Minute Practice</p>
                        </div>

                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Today's stress reset brings together three powerful maneuvers to help your body release stored tension, regulate your nervous system, and create space for healing. Follow along with the 16-minute video above and allow each movement to settle before progressing.
                          </p>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Today's Maneuvers</h4>
                          </div>
                          <div className="p-6">
                            <div className="grid sm:grid-cols-3 gap-4">
                              {[
                                { name: 'Vagus Nerve Reset', desc: 'Activating the body\'s primary calming pathway — the vagus nerve — to shift from stress mode into rest and regulation' },
                                { name: 'Totally Twisted', desc: 'A full-body rotational release that unwinds deep fascial holding patterns stored through the torso and spine' },
                                { name: 'Anti Gravity', desc: 'An inversion-based maneuver that uses gravity to decompress the spine, organs, and fascial lines' },
                              ].map((item, idx) => (
                                <div key={idx} className="bg-forest/5 rounded-xl p-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{idx + 1}</span>
                                    <p className="font-body font-medium text-forest text-sm">{item.name}</p>
                                  </div>
                                  <p className="text-stone-500 font-body text-xs leading-relaxed">{item.desc}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Anatomy Trains - The Fascial Lines */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">The Fascial Lines</h4>
                            <p className="text-cream/80 font-body text-sm mt-1">Based on Thomas Myers' Anatomy Trains</p>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-6">
                              These fascial lines represent continuous pathways of connective tissue that run through the body. Understanding them helps illustrate why releasing one area can create change throughout the entire system.
                            </p>

                            <div className="space-y-6">
                              {/* Deep Front Line */}
                              <div className="flex flex-col sm:flex-row gap-4 items-start bg-forest/5 rounded-xl p-4">
                                <div className="flex-shrink-0 mx-auto sm:mx-0">
                                  <img src="/deep-front-line.png" alt="Deep Front Line" className="h-48 w-auto object-contain rounded-lg" />
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-heading text-lg text-forest mb-2">Deep Front Line</h5>
                                  <p className="text-stone-600 font-body text-sm leading-relaxed">
                                    The body's core fascial pathway. It runs from the inner arch of the foot, up the inseam of the leg, through the pelvis, along the front of the spine, and up to the base of the skull. Intimately linked to breathing and walking, it connects the diaphragm, psoas, and deep stabilizers.
                                  </p>
                                </div>
                              </div>

                              {/* Superficial Front Line */}
                              <div className="flex flex-col sm:flex-row gap-4 items-start bg-forest/5 rounded-xl p-4">
                                <div className="flex-shrink-0 mx-auto sm:mx-0">
                                  <img src="/superficial-front-line.png" alt="Superficial Front Line" className="h-48 w-auto object-contain rounded-lg" />
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-heading text-lg text-forest mb-2">Superficial Front Line</h5>
                                  <p className="text-stone-600 font-body text-sm leading-relaxed">
                                    Runs from the tops of the toes, up through the shin, quadriceps, and abdominals, to the skull. It balances the Superficial Back Line and plays a key role in posture — flexing the trunk and hips and extending the knee.
                                  </p>
                                </div>
                              </div>

                              {/* Superficial Back Line */}
                              <div className="flex flex-col sm:flex-row gap-4 items-start bg-forest/5 rounded-xl p-4">
                                <div className="flex-shrink-0 mx-auto sm:mx-0">
                                  <img src="/superficial-back-line.png" alt="Superficial Back Line" className="h-48 w-auto object-contain rounded-lg" />
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-heading text-lg text-forest mb-2">Superficial Back Line</h5>
                                  <p className="text-stone-600 font-body text-sm leading-relaxed">
                                    Runs from the bottoms of the toes, around the heel, up the entire back of the body — calves, hamstrings, erector spinae — and over the head to the brow ridge. It supports the body in standing and spans all of the spinal curves.
                                  </p>
                                </div>
                              </div>

                              {/* Lateral Line */}
                              <div className="flex flex-col sm:flex-row gap-4 items-start bg-forest/5 rounded-xl p-4">
                                <div className="flex-shrink-0 mx-auto sm:mx-0">
                                  <img src="/lateral-line.png" alt="Lateral Line" className="h-48 w-auto object-contain rounded-lg" />
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-heading text-lg text-forest mb-2">Lateral Line</h5>
                                  <p className="text-stone-600 font-body text-sm leading-relaxed">
                                    Runs along each side of the body from the foot, up the outer leg and thigh, along the trunk in a woven pattern, to the skull behind the ear. It acts as a brake for lateral and rotational movements and balances left and right like tent guy-wires.
                                  </p>
                                </div>
                              </div>

                              {/* Spiral Line */}
                              <div className="flex flex-col sm:flex-row gap-4 items-start bg-forest/5 rounded-xl p-4">
                                <div className="flex-shrink-0 mx-auto sm:mx-0">
                                  <img src="/spiral-line.png" alt="Spiral Line" className="h-48 w-auto object-contain rounded-lg" />
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-heading text-lg text-forest mb-2">Spiral Line</h5>
                                  <p className="text-stone-600 font-body text-sm leading-relaxed">
                                    Winds around the body in a double helix — from the skull, across the back to the opposite shoulder, around the front of the torso, down across the hip, knee, and foot arch, then back again. It mediates rotations and helps maintain balance in all planes of movement.
                                  </p>
                                </div>
                              </div>

                              {/* Functional Lines */}
                              <div className="flex flex-col sm:flex-row gap-4 items-start bg-forest/5 rounded-xl p-4">
                                <div className="flex-shrink-0 mx-auto sm:mx-0">
                                  <img src="/functional-lines.png" alt="Functional Lines — Front and Back" className="h-48 w-auto object-contain rounded-lg" />
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-heading text-lg text-forest mb-2">Functional Lines</h5>
                                  <p className="text-stone-600 font-body text-sm leading-relaxed">
                                    These lines extend the arm lines across the body surface, connecting them through the trunk to the opposite leg. The front functional line runs from the shoulder across the chest and abdomen to the opposite hip. The back functional line crosses from the shoulder, down the back, through the glutes to the opposite leg. They are essential for powerful, coordinated movements like throwing, reaching, and walking.
                                  </p>
                                </div>
                              </div>

                              {/* Arm Lines */}
                              <div className="flex flex-col sm:flex-row gap-4 items-start bg-forest/5 rounded-xl p-4">
                                <div className="flex-shrink-0 mx-auto sm:mx-0">
                                  <img src="/arm-lines.png" alt="Arm Lines" className="h-48 w-auto object-contain rounded-lg" />
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-heading text-lg text-forest mb-2">Arm Lines</h5>
                                  <p className="text-stone-600 font-body text-sm leading-relaxed">
                                    Four distinct fascial lines run from the chest, shoulder, and spine through the arms to the fingers. They include the deep and superficial front arm lines, and the deep and superficial back arm lines. These lines connect the hands and arms to the ribcage and spine, influencing grip strength, shoulder stability, and the integration of upper body movement.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed">
                            Follow the video and allow each maneuver to settle. As you move through today's practice, consider the fascial lines above — notice how releasing one area creates a ripple of change through the whole body.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 9 Content - Stress Reset (id 11) */}
                  {selectedDay.id === 11 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Stress Reset</h3>
                          <p className="text-stone-500 font-body italic text-lg">Totally Twisted, Swinger, Pull Over, Sinus Reset</p>
                        </div>

                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Today's practice introduces a combination of rotational and decompression maneuvers alongside a sinus reset. These movements work together to unwind tension through the fascial spiral lines, open the chest and shoulders, and clear the head — literally and energetically.
                          </p>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Today's Maneuvers</h4>
                          </div>
                          <div className="p-6">
                            <div className="grid sm:grid-cols-2 gap-4">
                              {[
                                { name: 'Totally Twisted', desc: 'A full-body rotational release that unwinds deep fascial holding patterns through the torso and spine' },
                                { name: 'Swinger', desc: 'A rhythmic movement that builds momentum to release tension through the arms, shoulders, and lateral fascial lines' },
                                { name: 'Pull Over', desc: 'An overhead decompression that opens the chest, lengthens the front body, and creates space around the diaphragm' },
                                { name: 'Sinus Reset', desc: 'A targeted maneuver to relieve congestion and pressure through the facial fascia and nasal passages' },
                              ].map((item, idx) => (
                                <div key={idx} className="bg-forest/5 rounded-xl p-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{idx + 1}</span>
                                    <p className="font-body font-medium text-forest text-sm">{item.name}</p>
                                  </div>
                                  <p className="text-stone-500 font-body text-xs leading-relaxed">{item.desc}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Understanding Fascia - Educational Videos */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Understanding Fascia</h4>
                            <p className="text-cream/80 font-body text-sm mt-1">Short videos to give a broader sense of fascia</p>
                          </div>
                          <div className="p-6 space-y-6">
                            {/* Video 1 - What Is Fascia? */}
                            <div>
                              <h5 className="font-heading text-base text-forest mb-2">What is Fascia? — Human Garage</h5>
                              <div className="aspect-video bg-stone-900 rounded-xl overflow-hidden">
                                <iframe
                                  src="https://www.youtube.com/embed/99h3K_UhPmc"
                                  title="What Is Fascia? - Human Garage"
                                  className="w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            </div>

                            {/* Video 2 - Fascia & The Living Body */}
                            <div>
                              <h5 className="font-heading text-base text-forest mb-2">Fascia & The Living Body</h5>
                              <div className="aspect-video bg-stone-900 rounded-xl overflow-hidden">
                                <iframe
                                  src="https://www.youtube.com/embed/Dg5MBscgCro"
                                  title="Fascia & The Living Body"
                                  className="w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            </div>

                            {/* Video 3 - How We See the Body */}
                            <div>
                              <h5 className="font-heading text-base text-forest mb-2">How We See the Body & How It Moves — Human Garage</h5>
                              <div className="aspect-video bg-stone-900 rounded-xl overflow-hidden">
                                <iframe
                                  src="https://www.youtube.com/embed/HqSZFoBDqag"
                                  title="What is Fascia? How we see the body - Human Garage"
                                  className="w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed">
                            Follow the stress reset video and let each maneuver build on the last. Then take a moment to watch the Human Garage clip — it offers a beautiful foundation for understanding the body as a connected fascial system.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 10 Content - Organ Reset Restorative (id 12) */}
                  {selectedDay.id === 12 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Organ Reset Restorative</h3>
                          <p className="text-stone-500 font-body italic text-lg">Light Quality, Stress & Restorative Organ Work</p>
                        </div>

                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Today's session begins with an important look at light quality and its effect on stress — including a simple demo you can do at home to test the lights in your environment. From there, we move into a gentle, restorative organ reset practice designed to release deep tension held in the body's core systems.
                          </p>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">What to Expect Today</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <div className="bg-forest/5 rounded-xl p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">1</span>
                                <p className="font-body font-medium text-forest text-sm">Light Quality & Stress</p>
                              </div>
                              <p className="text-stone-500 font-body text-xs leading-relaxed">The first few minutes explore how the quality of light in your environment directly impacts your stress levels and nervous system. Includes a quick demo to test your own lights at home.</p>
                            </div>
                            <div className="bg-forest/5 rounded-xl p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">2</span>
                                <p className="font-body font-medium text-forest text-sm">Organ Reset Restorative</p>
                              </div>
                              <p className="text-stone-500 font-body text-xs leading-relaxed">A gentle, restorative practice that works with the organs and the fascia surrounding them. This helps release deep-held tension and supports your body's natural healing processes.</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed">
                            Watch the opening segment on light quality with curiosity — you might be surprised by what your lights reveal. Then settle into the restorative practice and let your body soften from the inside out.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 11 Content - The Three Foundations of Reset (id 13) */}
                  {selectedDay.id === 13 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Fascial Coffee - Upper Body Reset</h3>
                          <p className="text-stone-500 font-body italic text-lg">Twisty Wristy, Elbow Torque, Psoas Release, Vagus Nerve Reset</p>
                        </div>

                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Today's practice targets the upper body through a series of fascial maneuvers — wrist and elbow torque work, a psoas release, and a vagus nerve reset. These movements work together to unwind tension patterns in the arms, shoulders, and core while activating the body's rest-and-restore pathways.
                          </p>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Today's Maneuvers</h4>
                          </div>
                          <div className="p-6">
                            <div className="grid sm:grid-cols-2 gap-4">
                              {[
                                { name: 'Twisty Wristy', desc: 'A rotational release through the wrists and forearms that unwinds tension along the arm fascial lines' },
                                { name: 'Elbow Torque', desc: 'Targeted torque work at the elbow to release deep holding patterns in the upper arm and shoulder connection' },
                                { name: 'Psoas Release', desc: 'A deep core release targeting the psoas — the muscle that connects your upper and lower body and holds much of our stress response' },
                                { name: 'Vagus Nerve Reset', desc: 'A gentle reset that activates the vagus nerve, supporting the shift from stress mode into rest and recovery' },
                              ].map((item, idx) => (
                                <div key={idx} className="bg-forest/5 rounded-xl p-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{idx + 1}</span>
                                    <p className="font-body font-medium text-forest text-sm">{item.name}</p>
                                  </div>
                                  <p className="text-stone-500 font-body text-xs leading-relaxed">{item.desc}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">The Three Foundations of Reset</h4>
                            <p className="text-cream/80 font-body text-sm mt-1">Creating the conditions where healing can work</p>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              Before the body can truly repair and reorganize, it helps to address a few core foundations. These simple shifts reduce the overall load on the system and create the conditions where healing processes can work more effectively.
                            </p>
                          </div>
                        </div>

                        <div className="space-y-6 mb-8">
                          {/* Foundation 1 */}
                          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                            <div className="bg-terracotta px-6 py-4 flex items-center gap-3">
                              <span className="bg-white text-terracotta w-8 h-8 rounded-full flex items-center justify-center font-heading text-lg flex-shrink-0">1</span>
                              <h4 className="font-heading text-lg text-white">Reduce Chemical Load</h4>
                            </div>
                            <div className="p-6">
                              <p className="text-stone-600 font-body leading-relaxed">
                                Modern life exposes us to a constant stream of chemicals through food, water, air, and everyday products. While the body has remarkable detoxification systems, reducing unnecessary exposures can ease the burden on organs such as the liver, kidneys, and skin.
                              </p>
                              <p className="text-stone-600 font-body leading-relaxed mt-3">
                                Small shifts toward cleaner food, water, and household products can significantly lighten the body's workload.
                              </p>
                            </div>
                          </div>

                          {/* Foundation 2 */}
                          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                            <div className="bg-terracotta px-6 py-4 flex items-center gap-3">
                              <span className="bg-white text-terracotta w-8 h-8 rounded-full flex items-center justify-center font-heading text-lg flex-shrink-0">2</span>
                              <h4 className="font-heading text-lg text-white">Support Digestive Clearance</h4>
                            </div>
                            <div className="p-6">
                              <p className="text-stone-600 font-body leading-relaxed">
                                The digestive system plays a central role in health. It processes nutrients, communicates closely with the immune system, and eliminates waste from the body.
                              </p>
                              <p className="text-stone-600 font-body leading-relaxed mt-3">
                                Supporting digestion with hydration, whole foods, fiber, and balanced gut function helps maintain efficient elimination and overall internal balance.
                              </p>
                            </div>
                          </div>

                          {/* Foundation 3 */}
                          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                            <div className="bg-terracotta px-6 py-4 flex items-center gap-3">
                              <span className="bg-white text-terracotta w-8 h-8 rounded-full flex items-center justify-center font-heading text-lg flex-shrink-0">3</span>
                              <h4 className="font-heading text-lg text-white">Calm Inflammation</h4>
                            </div>
                            <div className="p-6 space-y-4">
                              <p className="text-stone-600 font-body leading-relaxed">
                                Inflammation is a natural protective response, but when it remains elevated for long periods it can contribute to fatigue, discomfort, and many chronic health challenges.
                              </p>
                              <p className="text-stone-600 font-body leading-relaxed">
                                Reducing inflammation often involves addressing multiple layers of the system: nutrition, sleep, movement, stress regulation, and environmental exposures.
                              </p>
                              <div className="bg-forest/5 rounded-xl p-5">
                                <p className="text-stone-600 font-body leading-relaxed">
                                  One often overlooked contributor is <span className="font-medium text-forest">mental and emotional load</span>. A mind that is constantly under pressure — worry, urgency, rumination, overstimulation — signals the nervous system to remain in a protective state. Over time this can amplify inflammatory processes throughout the body.
                                </p>
                                <p className="text-stone-600 font-body leading-relaxed mt-3">
                                  Creating moments of regulation, rest, and nervous system balance helps quiet this internal alarm system and supports a healthier baseline.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed text-lg">
                            When we reduce the load, support digestion, calm inflammation, and regulate emotions... the body often does what it was designed to do —
                          </p>
                          <p className="text-cream font-heading text-2xl mt-3">
                            restore balance.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 12 Content - The 3-6-9 Breath Pattern (id 14) */}
                  {selectedDay.id === 14 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">The 3-6-9 Breath Pattern</h3>
                          <p className="text-stone-500 font-body italic text-lg">The Sacred Layers Within the Practice</p>
                        </div>

                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed text-lg">
                            One of the reasons I LOVE Human Garage and Fascial Maneuvers soooooo much... is the layers that exist within the practice.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mt-4">
                            I know we move in weird ways and breathe here and there and do strange things... but below the surface is a finely curated experience. The pattern of breath embedded in the practice... is nothing short of sacred.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mt-4">
                            Combining breath work, fascia compression/expansion, torque, movement, exploration, affirmation and release all at the same time... boom... a healthier me and healthier you. And it's JUST SO DO-ABLE!
                          </p>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Why We Use the 3-6-9 Breath Pattern</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              In many movement and healing practices, breathing rhythms matter. One pattern we often use is the 3-6-9 breath cycle, inspired by ideas popularized by inventor Nikola Tesla, who believed the numbers 3, 6, and 9 held special significance in the patterns of nature.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              While the deeper mathematics can get quite complex, the simple idea is this: these numbers appear repeatedly in natural rhythms, geometry, and energetic cycles. Many traditions see them as representing a natural flow:
                            </p>

                            <div className="grid sm:grid-cols-3 gap-4 my-6">
                              <div className="bg-forest/5 rounded-xl p-5 text-center">
                                <span className="text-3xl font-heading text-terracotta">3</span>
                                <p className="font-body font-medium text-forest mt-2">Initiation</p>
                                <p className="text-stone-500 font-body text-sm mt-1">The beginning of movement or energy</p>
                              </div>
                              <div className="bg-forest/5 rounded-xl p-5 text-center">
                                <span className="text-3xl font-heading text-terracotta">6</span>
                                <p className="font-body font-medium text-forest mt-2">Expansion</p>
                                <p className="text-stone-500 font-body text-sm mt-1">The building or stabilizing phase</p>
                              </div>
                              <div className="bg-forest/5 rounded-xl p-5 text-center">
                                <span className="text-3xl font-heading text-terracotta">9</span>
                                <p className="font-body font-medium text-forest mt-2">Completion</p>
                                <p className="text-stone-500 font-body text-sm mt-1">Integration and release</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Applying It to Breath</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              When applied to breathing, this creates a smooth, progressive rhythm that helps guide the nervous system from activity toward calm.
                            </p>

                            <div className="bg-forest/5 rounded-xl p-5 space-y-3">
                              <div className="flex items-center gap-4">
                                <span className="bg-terracotta text-white w-10 h-10 rounded-full flex items-center justify-center font-heading text-lg flex-shrink-0">3</span>
                                <p className="font-body text-forest"><span className="font-medium">Inhale</span> for 3 counts</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="bg-terracotta text-white w-10 h-10 rounded-full flex items-center justify-center font-heading text-lg flex-shrink-0">6</span>
                                <p className="font-body text-forest"><span className="font-medium">Hold or expand</span> for 6 counts</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="bg-terracotta text-white w-10 h-10 rounded-full flex items-center justify-center font-heading text-lg flex-shrink-0">9</span>
                                <p className="font-body text-forest"><span className="font-medium">Exhale slowly</span> for 9 counts</p>
                              </div>
                            </div>

                            <p className="text-stone-600 font-body leading-relaxed">
                              The longer exhale is especially important. It gently activates the parasympathetic nervous system, which supports relaxation, regulation, and recovery.
                            </p>

                            <p className="text-stone-600 font-body leading-relaxed">This pattern creates a breath that feels:</p>
                            <ul className="space-y-1 ml-4">
                              <li className="text-stone-600 font-body flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0"></span> Steady</li>
                              <li className="text-stone-600 font-body flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0"></span> Intentional</li>
                              <li className="text-stone-600 font-body flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0"></span> Deeply regulating</li>
                            </ul>

                            <p className="text-stone-600 font-body leading-relaxed">
                              Over time, rhythmic breathing like this can help the body shift from stress mode into repair mode, which is one of the goals of this reset.
                            </p>
                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed mb-4">
                            Think of the 3-6-9 breath as a simple way to guide your body through a natural cycle:
                          </p>
                          <p className="text-cream font-heading text-2xl mb-4">
                            begin &rarr; expand &rarr; release
                          </p>
                          <p className="text-cream/90 font-body leading-relaxed italic">
                            And sometimes, the smallest rhythms are the ones that create the biggest shifts.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 14 Content - All Healing Is Self Healing (id 16) */}
                  {selectedDay.id === 16 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">All Healing Is Self Healing</h3>
                          <p className="text-stone-500 font-body italic text-lg">Why the Body Holds the Blueprint</p>
                        </div>

                        {/* Introduction */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Healing is not something done to the body, but something that arises from within it. This perspective is ancient, echoing through Chinese medicine, ancient movement systems, and lived human experience across cultures. The body inherently knows how to heal. The role of intervention is to remove what interferes with this natural process. At its core, all healing is self-healing.
                          </p>
                        </div>

                        {/* The Body Is Designed for Flow */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">The Body Is Designed for Flow</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              The body is not a machine of separate parts, but a living system of movement, communication, and circulation.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              In Chinese medicine, health is defined by flow: Qi moves through meridians, blood follows Qi, fluids follow blood, and consciousness follows all of it.
                            </p>
                            <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                              <p className="text-terracotta font-heading text-lg italic">Flow is life expressed.</p>
                            </div>
                            <p className="text-stone-600 font-body leading-relaxed">
                              Pain, dysfunction, and illness are signals that something has stopped moving correctly — not enemies to be fought.
                            </p>
                          </div>
                        </div>

                        {/* Stagnation */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Stagnation: The Root of Dis-Ease</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">Stagnation arises when circulation is compromised:</p>
                            <div className="flex flex-col items-center gap-2">
                              {['Energy that doesn\'t move becomes stagnant', 'Stagnation thickens', 'Thickened fluid pools', 'Pooling creates pressure, heat, or collapse'].map((step, i) => (
                                <React.Fragment key={i}>
                                  <div className="bg-forest/5 rounded-xl px-5 py-3 w-full max-w-md text-center">
                                    <p className="text-forest font-body text-sm">{step}</p>
                                  </div>
                                  {i < 3 && <ChevronDown size={16} className="text-terracotta" />}
                                </React.Fragment>
                              ))}
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3 mt-4">
                              {[
                                { name: 'Qi stagnation', effect: 'Affects organs and emotions' },
                                { name: 'Blood stagnation', effect: 'Creates congestion and pain' },
                                { name: 'Lymphatic stagnation', effect: 'Contributes to toxicity and inflammation' },
                                { name: 'Cerebrospinal fluid stagnation', effect: 'Dulls vitality, perception, and regulation' },
                              ].map((s, i) => (
                                <div key={i} className="bg-forest/5 rounded-xl p-4">
                                  <p className="font-body font-medium text-forest text-sm">{s.name}</p>
                                  <p className="text-stone-500 font-body text-xs mt-1">{s.effect}</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-stone-600 font-body leading-relaxed text-sm">
                              At first, the body compensates quietly with tightness, fatigue, subtle discomfort, or emotional flatness. Over time, if stagnation persists, the body communicates more clearly. The system is asking for movement to be restored — not indicating something is broken.
                            </p>
                          </div>
                        </div>

                        {/* Organs as Living Intelligence */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Organs as Living Intelligence</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              Organs are intelligent participants in the whole, not isolated objects. Each organ system has physiological roles, emotional expressions, movement patterns, and timing rhythms.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              Organs communicate continuously with each other. With present flow, organs self-regulate with remarkable precision.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              Symptoms often appear far from their source because the body adapts until it can no longer bear the load. The last place a symptom shows up is rarely the original problem.
                            </p>
                            <div className="bg-terracotta/10 rounded-xl p-4">
                              <p className="text-terracotta font-body text-sm italic">
                                Healing requires listening for where flow was interrupted — not just treating the discomfort's location.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* What Interrupts Flow */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">What Interrupts Flow?</h4>
                          </div>
                          <div className="p-6 space-y-6">
                            <p className="text-stone-600 font-body leading-relaxed">Energy flow is influenced by experience, stress, emotion, and thought.</p>

                            <div className="bg-forest/5 rounded-xl p-5">
                              <h5 className="font-heading text-forest mb-3">Thoughts and Beliefs</h5>
                              <ul className="space-y-2">
                                {['Repeated thoughts create repeated pathways in the body', 'Beliefs shape posture, breath, and muscular tone', 'When perception becomes rigid, the body organizes around that rigidity', 'Flexibility of mind allows flexibility of tissue', 'Chronic vigilance narrows circulation', 'Fixed narratives reduce options and flow'].map((p, i) => (
                                  <li key={i} className="text-stone-600 font-body text-sm flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0"></span>{p}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="bg-forest/5 rounded-xl p-5">
                              <h5 className="font-heading text-forest mb-3">Unprocessed Emotion</h5>
                              <ul className="space-y-2 mb-3">
                                {['Emotion is energy designed to move and resolve when felt and completed', 'When suppressed or judged unsafe, emotion settles into the body', 'Grief, anger, fear, and worry do not disappear when ignored — they become held patterns', 'These patterns alter circulation, tension, and organ function over time'].map((p, i) => (
                                  <li key={i} className="text-stone-600 font-body text-sm flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0"></span>{p}
                                  </li>
                                ))}
                              </ul>
                              <div className="bg-white rounded-lg p-3">
                                <p className="text-terracotta font-body text-sm italic text-center">
                                  What is unfelt becomes stored. What is stored becomes stagnant. What is stagnant eventually becomes symptomatic.
                                </p>
                              </div>
                            </div>

                            <div className="bg-forest/5 rounded-xl p-5">
                              <h5 className="font-heading text-forest mb-3">Trauma and Protective Stillness</h5>
                              <p className="text-stone-600 font-body text-sm leading-relaxed mb-2">
                                Trauma is defined not by the event itself, but by the lack of completion of the response to a threat. When the nervous system cannot respond to threat, it chooses stillness ("freeze"). This is an intelligent survival strategy, not dysfunction.
                              </p>
                              <p className="text-stone-600 font-body text-sm leading-relaxed mb-2">
                                Problems emerge when the body never receives the signal that safety has returned. In this state, expansion feels dangerous, circulation feels overwhelming, and rest feels unfamiliar.
                              </p>
                              <div className="bg-white rounded-lg p-3">
                                <p className="text-terracotta font-body text-sm italic text-center">
                                  Healing cannot occur where safety has not yet been established.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Why Healing Doesn't Work */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Why Healing Sometimes Doesn't Seem to Work</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              Some people don't feel the effects of practitioners' work — not because the work is ineffective or the person is resistant, but because the system may not feel safe enough to reorganize.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              If stillness has been the body's survival strategy, increased flow can feel threatening. The nervous system filters every intervention through the question: <span className="font-medium text-forest">"Is it safe to change?"</span>
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              Until the answer is "yes," healing efforts may pass through without integration. The body isn't rejecting healing — it's waiting for readiness.
                            </p>
                          </div>
                        </div>

                        {/* Healing as Permission */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Healing as Permission, Not Effort</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              Self-healing is not about doing more — it's often about doing less. Less bracing, less forcing, less overriding sensation and intuition.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              When the body feels permission to rest, feel, and move at its own pace, circulation resumes naturally. Fluids move, tissues hydrate, systems communicate again.
                            </p>
                            <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                              <p className="text-terracotta font-heading text-lg italic">Healing is not an act of will — it is a state of allowance.</p>
                            </div>
                          </div>
                        </div>

                        {/* Organ Emotions Table */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">The Organs as Emotional & Energetic Intelligence</h4>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              In Chinese medicine, organs are understood as centers of emotion, energy, and perception.
                            </p>
                            <div className="grid gap-3">
                              {[
                                { organ: 'Liver', energy: 'Flow, direction, vision', balanced: 'Motivation, creativity', stagnant: 'Anger, frustration' },
                                { organ: 'Heart', energy: 'Circulation, connection', balanced: 'Joy, clarity', stagnant: 'Anxiety, restlessness' },
                                { organ: 'Spleen', energy: 'Nourishment, grounding', balanced: 'Stability, trust', stagnant: 'Worry, overthinking' },
                                { organ: 'Lungs', energy: 'Breath, rhythm', balanced: 'Acceptance, inspiration', stagnant: 'Grief, sadness' },
                                { organ: 'Kidneys', energy: 'Vitality, essence', balanced: 'Courage, resilience', stagnant: 'Fear, insecurity' },
                                { organ: 'Stomach', energy: 'Receiving, processing', balanced: 'Comfort, satisfaction', stagnant: 'Disgust, anxiety' },
                                { organ: 'Small Intestine', energy: 'Discernment, clarity', balanced: 'Insight, discernment', stagnant: 'Confusion, overwhelm' },
                                { organ: 'Large Intestine', energy: 'Release, boundaries', balanced: 'Letting go, resolution', stagnant: 'Holding on, rigidity' },
                                { organ: 'Gallbladder', energy: 'Decision, action', balanced: 'Confidence, initiative', stagnant: 'Indecision, bitterness' },
                                { organ: 'Bladder', energy: 'Storage, release', balanced: 'Ease, adaptability', stagnant: 'Tension, fear' },
                              ].map((o, i) => (
                                <div key={i} className="bg-forest/5 rounded-xl p-4 grid sm:grid-cols-4 gap-2">
                                  <p className="font-body font-medium text-forest text-sm">{o.organ}</p>
                                  <p className="text-stone-500 font-body text-xs">{o.energy}</p>
                                  <p className="text-emerald-600 font-body text-xs">{o.balanced}</p>
                                  <p className="text-terracotta font-body text-xs">{o.stagnant}</p>
                                </div>
                              ))}
                            </div>
                            <div className="bg-terracotta/10 rounded-xl p-4 mt-4 text-center">
                              <p className="text-terracotta font-body text-sm italic">
                                When energy moves, emotions flow. When emotions flow, healing becomes possible.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Closing */}
                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">A Return to Inner Intelligence</h4>
                          <p className="text-cream/90 font-body leading-relaxed mb-4">
                            Every living system knows how to organize itself when interference is reduced. Seeds grow, wounds close, bones knit.
                          </p>
                          <p className="text-cream font-heading text-xl mb-4">
                            All healing is self-healing, because the body has always carried the blueprint.
                          </p>
                          <p className="text-cream/80 font-body leading-relaxed italic">
                            The work is not to control the body, but to listen closely to where it stopped moving and gently help it begin again.
                          </p>
                        </div>

                        {/* Recommended Reading */}
                        <div className="mt-8 bg-stone-50 rounded-2xl p-6">
                          <h4 className="font-heading text-forest mb-4">Recommended Reading</h4>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {[
                              { title: 'The Body Keeps the Score', author: 'Bessel van der Kolk' },
                              { title: 'The Biology of Belief', author: 'Bruce Lipton' },
                              { title: 'Eastern Body, Western Mind', author: 'Anodea Judith' },
                              { title: 'Waking the Tiger: Healing Trauma', author: 'Peter A. Levine' },
                            ].map((b, i) => (
                              <div key={i} className="bg-white rounded-xl p-3 border border-stone-200">
                                <p className="font-body font-medium text-forest text-sm">{b.title}</p>
                                <p className="text-stone-500 font-body text-xs">{b.author}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 18 Content - Etheric Body (id 20) */}
                  {selectedDay.id === 20 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">What is the Etheric Body?</h3>
                          <p className="text-stone-500 font-body italic text-lg">Understanding Our Luminous Blueprint</p>
                        </div>

                        {/* Jill's Intro */}
                        <div className="bg-terracotta/10 rounded-2xl p-6 mb-8 border border-terracotta/20">
                          <p className="text-stone-700 font-body leading-relaxed text-lg">
                            Now that some of the technique is out of the way... we can really dig into the energy of it all. Where the magic lives in our ENERGY system. This Etheric Body resource is a great way to start opening up ourselves to a greater view of the body.
                          </p>
                        </div>

                        {/* Introduction */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            The etheric body is often described as the first layer of the human aura — a subtle field of energy that surrounds, penetrates, and sustains the physical body. Many spiritual and esoteric traditions see it as the blueprint of our human form, a luminous double that helps shape, nourish, and repair the physical self.
                          </p>
                        </div>

                        {/* Nature of the Etheric Body */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">The Nature of the Etheric Body</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              Unlike flesh and bone, the etheric body is said to be composed of finer, more subtle matter.
                            </p>
                            <ul className="space-y-2">
                              {[
                                'Connects with the physical body through the chakras and energy channels',
                                'Distributes life force to the cells, organs, and systems',
                                'Considered essential for health, vitality, and overall well-being',
                                'Some people with heightened spiritual sensitivity can perceive it as a faint glow around the body',
                              ].map((p, i) => (
                                <li key={i} className="text-stone-600 font-body text-sm flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0"></span>{p}
                                </li>
                              ))}
                            </ul>
                            <div className="bg-forest/5 rounded-xl p-4">
                              <p className="text-stone-600 font-body text-sm leading-relaxed">
                                Many traditions teach that it can be strengthened through practices such as meditation, breathwork, energy healing, crystal work, or conscious movement.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Origins of the Word */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Origins of the Word 'Etheric'</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              The term comes from the Greek word <span className="font-medium text-forest">'aither'</span>, meaning 'pure sky' or 'upper air.'
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              In ancient cosmology, aither was the fifth element, believed to fill the heavens and act as the medium for light itself.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              Later spiritual systems borrowed this concept to describe the unseen substance that underlies and sustains physical life.
                            </p>
                          </div>
                        </div>

                        {/* Esoteric Traditions */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Esoteric Traditions</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            {[
                              { name: 'Theosophical Tradition', desc: 'Developed further in the late 19th century, teaching that all beings are fundamentally spiritual and that subtle bodies provide the bridge between physical life and higher planes of existence.' },
                              { name: 'Hermetic Order of the Golden Dawn', desc: 'Wove the etheric body into teachings on ceremonial magic and the Qabalah, emphasizing its role in spiritual development and mystical practice.' },
                              { name: 'Gnosticism', desc: 'Believed the soul was a divine spark temporarily bound in the material world, and that true liberation came through gnosis (direct spiritual knowledge).' },
                            ].map((t, i) => (
                              <div key={i} className="bg-forest/5 rounded-xl p-4">
                                <p className="font-body font-medium text-forest text-sm mb-1">{t.name}</p>
                                <p className="text-stone-500 font-body text-xs leading-relaxed">{t.desc}</p>
                              </div>
                            ))}
                            <p className="text-stone-600 font-body text-sm italic">
                              Both perspectives highlight the human capacity to reach beyond material reality through layers of subtle energy.
                            </p>
                          </div>
                        </div>

                        {/* Energy Field */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">The Etheric Body as an Energy Field</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              In practical terms, the etheric body is often described as an energetic buffer.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              It receives and transmits forces from higher dimensions into the nervous, endocrine, and circulatory systems, revitalizing the cells of the body.
                            </p>
                            <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                              <p className="text-terracotta font-heading italic">
                                When well-aligned, it supports mental clarity, physical health, emotional balance, and spiritual growth.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Living Blueprint */}
                        <div className="mb-8 bg-forest/5 rounded-2xl p-6">
                          <h4 className="font-heading text-forest mb-3">A Living Blueprint</h4>
                          <p className="text-stone-600 font-body leading-relaxed mb-3">
                            Ultimately, the etheric body is seen as a living template — a subtle energy field that both mirrors and informs the physical body.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed">
                            In harmony, it not only sustains our health but also awakens us to the deeper layers of our being.
                          </p>
                        </div>

                        {/* Aura Layers */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">The Clear Human Aura</h4>
                            <p className="text-cream/70 font-body text-sm mt-1">The human energy field consists of multiple layers</p>
                          </div>
                          <div className="p-6">
                            <div className="flex flex-col items-center gap-2">
                              {[
                                { name: 'Soul Star', desc: 'Receives incoming universal energy', color: 'bg-violet-100 text-violet-700' },
                                { name: 'Crown Chakra', desc: 'Radiates soul star energy', color: 'bg-purple-100 text-purple-700' },
                                { name: 'Etheric Body', desc: 'First layer, sustains physical form', color: 'bg-teal-100 text-teal-700' },
                                { name: 'Emotional Body', desc: 'Holds emotional energy', color: 'bg-rose-100 text-rose-700' },
                                { name: 'Mental Body', desc: 'Contains thought patterns', color: 'bg-amber-100 text-amber-700' },
                                { name: 'Astral Body', desc: 'Bridge to higher dimensions', color: 'bg-indigo-100 text-indigo-700' },
                                { name: 'Electromagnetic Field', desc: 'Surrounds and protects the first four spiritual bodies', color: 'bg-sky-100 text-sky-700' },
                                { name: 'Earth Star', desc: 'Receives incoming earth energy', color: 'bg-emerald-100 text-emerald-700' },
                              ].map((layer, i) => (
                                <div key={i} className={`w-full max-w-md rounded-xl p-3 text-center ${layer.color}`}>
                                  <p className="font-body font-medium text-sm">{layer.name}</p>
                                  <p className="font-body text-xs opacity-70">{layer.desc}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Constriction & Expansion */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Expansion & The Inner Environment</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              Our etheric body responds to our inner state. When we hold contracted beliefs, our energy field contracts too.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3">
                              {[
                                "I'm not enough yet",
                                "I need to be different to be okay",
                                "Something is wrong with me",
                                "I can't trust myself",
                              ].map((b, i) => (
                                <div key={i} className="bg-stone-100 rounded-xl p-3 text-center">
                                  <p className="text-stone-600 font-body text-sm italic">"{b}"</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-stone-600 font-body leading-relaxed">
                              When we don't like ourselves, even slightly, life tightens. The nervous system senses threat. The body contracts. The mind narrows to protection and control.
                            </p>
                            <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                              <p className="text-terracotta font-body text-sm italic">
                                Pain doesn't just come from injury or circumstance. It often comes from how tightly we're holding ourselves while living our lives.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Expansion Practice */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Expansion Isn't Something We Force</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              Expansion isn't about becoming better, higher, or more evolved. It's about removing the inner pressure.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">It shows up when:</p>
                            <ul className="space-y-2">
                              {[
                                'We soften our grip on self-judgment',
                                'We stop fighting our own humanity',
                                'We feel safe enough to feel what\'s actually here',
                                'We allow curiosity instead of condemnation',
                              ].map((p, i) => (
                                <li key={i} className="text-stone-600 font-body text-sm flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0"></span>{p}
                                </li>
                              ))}
                            </ul>
                            <div className="bg-forest/5 rounded-xl p-4 text-center">
                              <p className="text-forest font-heading italic">Expansion doesn't require confidence. It requires friendliness.</p>
                            </div>
                          </div>
                        </div>

                        {/* Simplest Truth */}
                        <div className="mb-8 bg-forest/5 rounded-2xl p-6">
                          <h4 className="font-heading text-forest mb-3">The Simplest Truth</h4>
                          <p className="text-stone-600 font-body leading-relaxed mb-3">
                            If we like ourselves, life feels good. If we don't like ourselves, life does NOT feel good.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed">
                            Most people would never speak to a friend the way they speak to themselves. Yet we wonder why we feel contracted, tired, anxious, or disconnected.
                          </p>
                        </div>

                        {/* Best Friend */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Are We Our Own Best Friend?</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              Being your own best friend doesn't mean constant positivity. It means non-abandonment.
                            </p>
                            <ul className="space-y-2">
                              {[
                                'Staying present with yourself when things feel messy',
                                'Not making yourself wrong for being human',
                                'Listening instead of correcting',
                                'Offering support before solutions',
                              ].map((p, i) => (
                                <li key={i} className="text-stone-600 font-body text-sm flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0"></span>{p}
                                </li>
                              ))}
                            </ul>
                            <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                              <p className="text-terracotta font-heading italic">
                                Expansion happens when the inner environment becomes safe. Not perfect. Safe.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Closing */}
                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">Today's Invitation</h4>
                          <p className="text-cream/90 font-body leading-relaxed mb-4">
                            Begin to notice your inner dialogue today. Not to change it — just to notice it.
                          </p>
                          <p className="text-cream font-heading text-xl mb-4">
                            Where might you be holding yourself tightly?
                          </p>
                          <p className="text-cream/80 font-body leading-relaxed italic">
                            What would it feel like to soften, even just a little?
                          </p>
                        </div>

                        {/* Recommended Reading */}
                        <div className="mt-8 bg-stone-50 rounded-2xl p-6">
                          <h4 className="font-heading text-forest mb-4">Recommended Reading</h4>
                          <div className="grid sm:grid-cols-3 gap-3">
                            {[
                              { title: 'Eastern Body, Western Mind', author: 'Anodea Judith' },
                              { title: 'The Subtle Body', author: 'Cyndi Dale' },
                              { title: 'Hands of Light', author: 'Barbara Brennan' },
                            ].map((b, i) => (
                              <div key={i} className="bg-white rounded-xl p-3 border border-stone-200">
                                <p className="font-body font-medium text-forest text-sm">{b.title}</p>
                                <p className="text-stone-500 font-body text-xs">{b.author}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day 21 Content - Introduction to Metaphysics (id 23) */}
                  {selectedDay.id === 23 && (
                    <div>
                      {/* Video */}
                      <div className="aspect-video bg-stone-900">
                        <iframe
                          src={selectedDay.videoUrl}
                          title={selectedDay.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      <div className="p-6 md:p-8">
                        <div className="text-center mb-10">
                          <h3 className="font-heading text-3xl text-forest mb-3">Introduction To Metaphysics</h3>
                          <p className="text-stone-500 font-body italic text-lg">When the Body Speaks, Listen</p>
                        </div>

                        {/* Opening */}
                        <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                          <p className="text-stone-600 font-body leading-relaxed">
                            Most of us prefer to think of the body as just a body — a collection of bones, muscles, and organs that sometimes cooperate and sometimes don't. When pain shows up, we usually look for the nearest physical explanation: too much stress, a strained muscle, a hormone out of balance. Rarely do we stop to ask: <span className="font-medium text-forest italic">what else might this be telling me?</span>
                          </p>
                        </div>

                        {/* The Body Never Lies */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">The Body Never Lies</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              The truth is, our bodies are not only physical — they are plasma-and-particle vessels, alive with energy, constantly transmitting signals from deeper layers of who we are. If the mind is quick to spin stories, hide truths, or distract us with endless chatter, the body is refreshingly direct. It never lies.
                            </p>
                            <p className="text-stone-600 font-body leading-relaxed">
                              Much of what the body reveals is rooted in the subconscious — or what some would call the unconscious — the unseen layers where emotions, memories, and stress patterns quietly live. We may not always be aware of what we've tucked away, but the body is. It faithfully translates those hidden experiences into tangible form: tight shoulders, a heavy chest, a restless gut, or even long-term illness.
                            </p>
                            <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                              <p className="text-terracotta font-heading italic">And when you start to look for it, you realize… we already know this. We've just never been taught to trust it.</p>
                            </div>
                          </div>
                        </div>

                        {/* Body Speaking */}
                        <div className="mb-8 bg-white rounded-2xl border-2 border-terracotta/30 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">The Body Speaking in Real Time</h4>
                          </div>
                          <div className="p-6">
                            <div className="grid sm:grid-cols-2 gap-3">
                              {[
                                { signal: 'Your stomach drops', meaning: 'before bad news lands' },
                                { signal: 'Your chest tightens', meaning: 'when you\'re anxious or overwhelmed' },
                                { signal: 'Your shoulders creep up', meaning: 'when you\'re carrying too much' },
                                { signal: 'Your jaw clenches', meaning: 'when something feels unfair or unspoken' },
                                { signal: 'Your throat tightens', meaning: 'when you\'re holding back words' },
                                { signal: 'You feel butterflies', meaning: 'when you\'re excited or stepping into something new' },
                                { signal: 'You feel physically heavy', meaning: 'during grief' },
                                { signal: 'You feel energized and lighter', meaning: 'when something feels aligned' },
                              ].map((s, i) => (
                                <div key={i} className="bg-forest/5 rounded-xl p-3">
                                  <p className="font-body text-forest text-sm font-medium">{s.signal}</p>
                                  <p className="text-stone-500 font-body text-xs">{s.meaning}</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-stone-600 font-body leading-relaxed mt-4 text-center italic">
                              These aren't random reactions — they are the body speaking in real time.
                            </p>
                          </div>
                        </div>

                        {/* Symptoms as Messages */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-forest px-6 py-4">
                            <h4 className="font-heading text-lg text-cream">Symptoms as Messages</h4>
                          </div>
                          <div className="p-6 space-y-4">
                            <p className="text-stone-600 font-body leading-relaxed">
                              From a metaphysical perspective, symptoms are not random flaws or punishments. They are messages — gentle (and sometimes not-so-gentle) invitations to listen more deeply. They ask:
                            </p>
                            <div className="space-y-2">
                              {['What is unspoken?', 'What is unprocessed?', 'What part of me needs to be heard or released?'].map((q, i) => (
                                <div key={i} className="bg-forest/5 rounded-xl p-3 flex items-center gap-3">
                                  <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{i+1}</span>
                                  <p className="text-forest font-body text-sm italic">{q}</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-stone-600 font-body leading-relaxed">
                              This approach does not replace medical care. Instead, it adds another layer of wisdom. It invites us to see the body not as something that betrays us, but as something that communicates with us — consistently, honestly, and often quite brilliantly.
                            </p>
                            <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                              <p className="text-terracotta font-heading italic">So when the body speaks — through sensation, tension, or illness — perhaps the most powerful thing we can do… is listen.</p>
                            </div>
                          </div>
                        </div>

                        {/* Body Signals Guide */}
                        <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="bg-terracotta px-6 py-4">
                            <h4 className="font-heading text-lg text-white">Common Body Signals & What They May Reflect</h4>
                            <p className="text-cream/80 font-body text-sm mt-1">A simple guide to help you start listening</p>
                          </div>
                          <div className="p-6">
                            <p className="text-stone-600 font-body leading-relaxed mb-4">
                              As you begin to tune into your body, you may notice patterns in where you feel things. These are not rules, but gentle reference points — places to get curious rather than certain.
                            </p>
                            <div className="grid gap-2">
                              {[
                                { area: 'Head', meaning: 'Mental overload, overthinking, pressure, trying to control outcomes' },
                                { area: 'Jaw', meaning: 'Suppressed frustration, unspoken words, holding tension' },
                                { area: 'Neck', meaning: 'Resistance, difficulty seeing another perspective, stubbornness' },
                                { area: 'Shoulders', meaning: 'Carrying responsibility, burdens, "holding it all together"' },
                                { area: 'Chest / Heart Space', meaning: 'Grief, love, emotional openness or protection' },
                                { area: 'Upper Back', meaning: 'Feeling unsupported, unseen, or emotionally alone' },
                                { area: 'Lower Back', meaning: 'Security concerns (money, safety, stability)' },
                                { area: 'Stomach / Gut', meaning: 'Processing emotions, anxiety, "gut instincts," worry' },
                                { area: 'Solar Plexus', meaning: 'Personal power, control, self-worth' },
                                { area: 'Hips', meaning: 'Stored emotions, fear of moving forward, old emotional residue' },
                                { area: 'Pelvis / Reproductive', meaning: 'Creativity, relationships, intimacy, identity' },
                                { area: 'Hands', meaning: 'Control, giving/receiving, holding on vs letting go' },
                                { area: 'Legs', meaning: 'Forward movement, direction in life, independence' },
                                { area: 'Knees', meaning: 'Flexibility, ego, ability to bend and adapt' },
                                { area: 'Feet', meaning: 'Grounding, stability, trust in your path' },
                              ].map((b, i) => (
                                <div key={i} className="flex items-start gap-3 bg-forest/5 rounded-xl p-3">
                                  <span className="bg-forest text-cream text-xs font-heading px-2 py-1 rounded-full flex-shrink-0 min-w-[90px] text-center">{b.area}</span>
                                  <p className="text-stone-600 font-body text-sm">{b.meaning}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Closing */}
                        <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-center">
                          <h4 className="font-heading text-xl mb-4 text-terracotta">A Gentle Reminder</h4>
                          <p className="text-cream/90 font-body leading-relaxed">
                            You don't need to analyze every sensation. Sometimes simply noticing — "oh, my shoulders are tight today" — is enough to begin shifting the pattern.
                          </p>
                          <p className="text-cream font-heading text-xl mt-4">
                            Awareness softens what force cannot.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Introduction Content (Day 1 / id 1) */}
                  {selectedDay.id === 1 && (
                    <div>
                      {/* Introduction Video */}
                      <div className="aspect-video bg-stone-900">
                        {selectedDay.videoUrl ? (
                          <iframe
                            src={selectedDay.videoUrl}
                            title={selectedDay.title}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-20 h-20 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Video size={32} className="text-stone-600" />
                              </div>
                              <p className="text-stone-500 font-body">Video coming soon</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="p-6 md:p-8">
                      {/* Welcome Header */}
                      <div className="text-center mb-10">
                        <p className="text-terracotta font-body text-lg italic mb-4">Welcome. I'm so glad you're here.</p>
                        <p className="text-stone-600 font-body leading-relaxed max-w-2xl mx-auto">
                          You've chosen to dedicate the next 28 days to your body, your nervous system, and your capacity to feel more at home in yourself. This is not about pushing. It's about consistent, intelligent input. Small daily resets that create measurable shifts.
                        </p>
                      </div>

                      <div className="bg-forest/5 rounded-2xl p-6 mb-8">
                        <h3 className="font-heading text-xl text-forest mb-4">Before you begin, a few important notes:</h3>
                      </div>

                      {/* Section 1: Wellness Snapshot */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-terracotta/10 px-6 py-4 border-b border-stone-200">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-terracotta rounded-full flex items-center justify-center text-white font-heading text-lg">1</div>
                            <h4 className="font-heading text-xl text-forest">Complete Your Wellness Snapshot</h4>
                          </div>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            Please take a few minutes to complete your Wellness Snapshot (you'll find it in the next section).
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            <span className="font-medium text-forest">This gives you a baseline.</span><br />
                            Energy. Sleep. Pain. Mood. Stress levels. Mobility.
                          </p>
                          <p className="text-stone-500 font-body italic">
                            It doesn't need to be perfect. Just honest.
                          </p>
                          <p className="text-stone-600 font-body mt-4">
                            We'll revisit this at the end so you can clearly see what has shifted.
                          </p>
                        </div>
                      </div>

                      {/* Section 2: Before & After Photos */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-amber-50 px-6 py-4 border-b border-stone-200">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-heading text-lg">2</div>
                            <div>
                              <h4 className="font-heading text-xl text-forest">Consider Before & After Photos</h4>
                              <span className="text-amber-600 font-body text-sm">Optional, but powerful</span>
                            </div>
                          </div>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            You may wish to take simple before photos today:
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                            {['Front', 'Side', 'Back', 'Relaxed posture'].map((angle) => (
                              <div key={angle} className="bg-stone-50 rounded-lg p-3 text-center">
                                <span className="text-stone-600 font-body text-sm">{angle}</span>
                              </div>
                            ))}
                          </div>
                          <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                            <p className="text-emerald-700 font-body text-sm leading-relaxed">
                              <span className="font-medium">Fascia responds quickly.</span> Alignment changes. Puffiness reduces. Shape refines. Even your eyes can look different.
                            </p>
                          </div>
                          <p className="text-stone-500 font-body italic text-sm">
                            You don't have to share these with anyone. They are for you.
                          </p>
                        </div>
                      </div>

                      {/* Section 3: What to Expect */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-violet-50 px-6 py-4 border-b border-stone-200">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-violet-500 rounded-full flex items-center justify-center text-white font-heading text-lg">3</div>
                            <h4 className="font-heading text-xl text-forest">What to Expect Each Day</h4>
                          </div>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            Each day includes a short <span className="font-medium text-forest">15–20 minute Stress Reset video</span>.
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            Some days will also include a <span className="font-medium text-forest">Live Zoom session</span>. If you miss the live session, no problem. Simply open that day's lesson and complete the Stress Reset recording.
                          </p>
                          <p className="text-stone-500 font-body text-sm italic mb-6">
                            If the live discussion includes personal sharing from participants, that portion may remain private within our collective space. The teaching and movement portions will always be available inside the program.
                          </p>
                          <div className="grid md:grid-cols-3 gap-4">
                            {[
                              { icon: '🎥', text: 'Join live' },
                              { icon: '▶️', text: 'Watch the replay' },
                              { icon: '🏠', text: 'Follow along on your own schedule' }
                            ].map((item, idx) => (
                              <div key={idx} className="bg-stone-50 rounded-lg p-4 text-center">
                                <span className="text-2xl mb-2 block">{item.icon}</span>
                                <span className="text-stone-600 font-body text-sm">{item.text}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-forest font-body font-medium text-center mt-6">
                            Consistency matters more than intensity.
                          </p>
                        </div>
                      </div>

                      {/* Section 4: Supportive Tool */}
                      <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="bg-blue-50 px-6 py-4 border-b border-stone-200">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-heading text-lg">4</div>
                            <h4 className="font-heading text-xl text-forest">This Is a Supportive Tool</h4>
                          </div>
                        </div>
                        <div className="p-6">
                          <p className="text-stone-600 font-body leading-relaxed mb-4">
                            This reset is not something you "complete."<br />
                            <span className="font-medium text-forest">It's something you build.</span>
                          </p>
                          <p className="text-stone-600 font-body leading-relaxed mb-6">
                            These practices are designed to become a tool you can return to anytime your system feels overloaded, stuck, tight, or off.
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {['Everything is optional', 'Modify as needed', 'Listen to your body'].map((text) => (
                              <span key={text} className="bg-stone-100 text-stone-600 px-4 py-2 rounded-full font-body text-sm">
                                {text}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Section 5: Enjoy */}
                      <div className="mb-8 bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-white">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white font-heading text-lg">5</div>
                          <h4 className="font-heading text-xl">Enjoy the Process</h4>
                        </div>
                        <p className="font-body leading-relaxed mb-6 text-cream/90">
                          This is an invitation back into yourself.
                        </p>
                        <div className="space-y-2 mb-6 text-cream/80 font-body">
                          <p>Some days will feel subtle.</p>
                          <p>Some days will feel profound.</p>
                          <p className="font-medium text-white">All of it counts.</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-6 backdrop-blur">
                          <p className="text-cream font-body text-center">
                            You are building <span className="font-medium">regulation</span>.<br />
                            You are building <span className="font-medium">awareness</span>.<br />
                            You are building <span className="font-medium">resilience</span>.
                          </p>
                        </div>
                      </div>

                      {/* Welcome Message */}
                      <div className="text-center py-8">
                        <p className="font-heading text-3xl text-forest mb-2">Welcome to Day 1</p>
                        <span className="text-4xl">🌿</span>
                      </div>
                    </div>
                    </div>
                  )}

                  {/* My Notes Section */}
                  <div className="px-6 md:px-8 pb-4">
                    <div className="bg-stone-50 rounded-2xl p-5 border border-stone-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <StickyNote size={16} className="text-terracotta" />
                          <h4 className="font-heading text-sm text-forest">My Notes</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          {noteSaved && <span className="text-emerald-500 font-body text-xs">Saved!</span>}
                          <button
                            onClick={saveNote}
                            disabled={noteSaving}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-forest text-cream rounded-full text-xs font-body hover:bg-forest-dark transition-colors disabled:opacity-50"
                            data-testid="save-note-btn"
                          >
                            <Save size={12} />
                            {noteSaving ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                      <textarea
                        value={currentNote}
                        onChange={(e) => setCurrentNote(e.target.value)}
                        placeholder="Write your thoughts, experiences, or reflections for this lesson..."
                        className="w-full bg-white border border-stone-200 rounded-xl p-3 text-forest font-body text-sm min-h-[100px] resize-y focus:outline-none focus:border-terracotta placeholder:text-stone-400"
                        data-testid="lesson-note-input"
                      />
                    </div>
                  </div>

                  {/* Navigation - shown for all days */}
                  <div className="p-6 md:p-8 pt-0">
                    <div className="flex items-center justify-between pt-6 border-t border-stone-200">
                      {(() => {
                        const currentIndex = programDays.findIndex(d => d.id === selectedDay.id);
                        return currentIndex > 0 ? (
                          <button
                            onClick={() => setSelectedDay(programDays[currentIndex - 1])}
                            className="flex items-center gap-2 text-stone-500 hover:text-forest transition-colors font-body"
                          >
                            <ArrowLeft size={18} />
                            Previous
                          </button>
                        ) : (
                          <div />
                        );
                      })()}
                      
                      {(() => {
                        const currentIndex = programDays.findIndex(d => d.id === selectedDay.id);
                        return currentIndex < programDays.length - 1 ? (
                          <button
                            onClick={() => setSelectedDay(programDays[currentIndex + 1])}
                            className="flex items-center gap-2 bg-terracotta text-white px-6 py-2 rounded-full hover:bg-terracotta-dark transition-colors font-body"
                          >
                            Next Lesson
                            <ChevronRight size={18} />
                          </button>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </motion.div>
                )
              ) : (
                /* Welcome State */
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-lg p-8 text-center"
                >
                  <div className="w-24 h-24 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Play size={40} className="text-terracotta" />
                  </div>
                  <h2 className="font-heading text-2xl text-forest mb-3">Welcome to Your Stress Reset Journey</h2>
                  <p className="text-stone-600 font-body mb-6 max-w-lg mx-auto">
                    Select a lesson from the sidebar to begin. We recommend starting with the Introduction 
                    to get the most out of this transformational 28-day program.
                  </p>
                  <button
                    onClick={() => setSelectedDay(programDays[0])}
                    className="inline-flex items-center gap-2 bg-forest text-cream px-8 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors"
                  >
                    Start with Introduction
                    <ChevronRight size={18} />
                  </button>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-stone-200">
                    <div>
                      <p className="font-heading text-2xl text-terracotta">31</p>
                      <p className="text-stone-500 font-body text-sm">Total Lessons</p>
                    </div>
                    <div>
                      <p className="font-heading text-2xl text-terracotta">28</p>
                      <p className="text-stone-500 font-body text-sm">Days of Practice</p>
                    </div>
                    <div>
                      <p className="font-heading text-2xl text-terracotta">0</p>
                      <p className="text-stone-500 font-body text-sm">Completed</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Restart Program Confirmation Modal */}
      {showRestartConfirm && (
        <div
          className="fixed inset-0 bg-forest/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => !restartInFlight && setShowRestartConfirm(false)}
          data-testid="restart-confirm-modal"
        >
          <div
            className="bg-cream rounded-2xl max-w-md w-full p-7 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forest/10 text-forest mb-4">
              <RotateCcw size={20} />
            </div>
            <h3 className="font-heading text-2xl text-forest mb-2">
              Restart the 28 Day Journey?
            </h3>
            <p className="text-stone-600 font-body text-sm leading-relaxed mb-5">
              This will relock every day and make <strong>today Day 1</strong>. You'll see one day unlock
              each day, just like the first time. Your notes, favorites, and wellness snapshots are kept.
            </p>
            <div className="flex flex-wrap gap-3 justify-end">
              <button
                onClick={() => setShowRestartConfirm(false)}
                disabled={restartInFlight}
                className="px-5 py-2.5 rounded-full font-body text-sm text-stone-600 hover:text-forest border border-stone-300 hover:border-forest transition-colors disabled:opacity-50"
                data-testid="restart-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleRestartProgram}
                disabled={restartInFlight}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm bg-forest text-cream hover:bg-forest-dark transition-colors disabled:opacity-60"
                data-testid="restart-confirm"
              >
                {restartInFlight ? 'Restarting…' : (<><RotateCcw size={14} /> Restart Now</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FasciaResetProgramPage;
